import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { fmtUGX, fmtNum, shortDateTime } from "@/lib/format";
import {
  Banknote, Building2, Receipt, Shield, Download, Play, Archive, Eye, Activity, FileText,
  LayoutDashboard, Users, Store, Bus, HeartPulse, BookOpen, MessageSquare, Bot, Lock,
  Server, BarChart3, Settings2, Crown, Search, MapPin, AlertTriangle, CheckCircle2,
  TrendingUp, Wallet, GraduationCap, Sparkles, RefreshCw, ChevronRight, Puzzle, ShieldCheck,
  Palette, Menu, X,
} from "lucide-react";
import { toast } from "sonner";
import { LiveBusMap } from "@/components/live-bus-map";
import { motion, AnimatePresence } from "framer-motion";
import jsPDF from "jspdf";

import { ParentDashboard } from "./parent-dashboard";
import { StudentDashboard } from "./student-dashboard";
import { AuditLogsPanel as SuperAudit } from "@/features/audit/audit-logs-panel";
import { InvitationsPanel as SuperInvitations } from "@/features/invitations/invitations-panel";
import { MerchantDashboard } from "./merchant-dashboard";
import { SchoolsMap, type SchoolPin } from "@/components/schools-map";

import { GeoIntelligence } from "./geo-intelligence";
import { FinanceCommandCenter } from "./finance-command-center";
import { MerchantsCommandCenter } from "./merchants-command-center";
import { SubscriptionsCommandCenter } from "./subscriptions-command-center";
import { TransportCommandCenter } from "./transport-command-center";
import { SchoolsCommandCenter } from "./schools-command-center";
import { SuperadminSchoolAdminsConsole } from "./superadmin/school-admins-console";
import { HealthCareModule } from "@/features/school-admin/health-care-module";
import { FeatureModulesConsole } from "./feature-modules-console";
import { SuperadminCRM } from "./superadmin-crm";
import { TenantBrandingConsole } from "./superadmin/tenant-branding-console";
import { TenantBrandQuickSettings } from "./tenant-brand-quick-settings";
import { TenantVisibilitySettings } from "./tenant-visibility-settings";
import { TenantAnalyticsConsole } from "./superadmin/tenant-analytics-console";
import { SectionHero, StatTile as VisStatTile, Panel, BarRow, RingGauge, Sparkline, LiveDot, toneVar } from "./superadmin/console-visuals";
const StatTile = VisStatTile;



const VIEW_AS_KEY = "schoolos.viewAs";
const SECTION_KEY = "schoolos.adminSection";

type Section =
  | "exec" | "geo" | "schools" | "admins" | "users" | "finance" | "merchants" | "transport"
  | "boarding" | "health" | "academics" | "comms" | "ai" | "compliance"
  | "security" | "billing" | "modules" | "platform" | "bi" | "godmode" | "branding" | "tenant_analytics";

// ── Custom SVG brand-icons for each nav section ──────────────────────────────
function NavIcon({ id, active }: { id: Section; active: boolean }) {
  const cls = `h-[18px] w-[18px] flex-shrink-0 transition-all duration-200 ${active ? "drop-shadow-[0_0_6px_rgba(255,255,255,0.7)]" : ""}`;
  const c = active ? "#fff" : "rgba(255,255,255,0.65)";
  const s = { stroke: c, fill: "none", strokeWidth: 1.7, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  switch (id) {
    case "exec": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <rect x="2" y="2" width="7" height="7" rx="1.5" {...s} fill={active?"rgba(255,255,255,0.15)":undefined}/>
        <rect x="11" y="2" width="7" height="3.5" rx="1" {...s}/>
        <rect x="11" y="7.5" width="7" height="1.5" rx="0.75" {...s}/>
        <rect x="2" y="11" width="16" height="3" rx="1" {...s} fill={active?"rgba(255,255,255,0.1)":undefined}/>
        <rect x="2" y="16" width="7" height="2" rx="1" {...s}/>
        <rect x="11" y="16" width="7" height="2" rx="1" {...s}/>
      </svg>);
    case "geo": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <circle cx="10" cy="8.5" r="2.2" {...s}/>
        <path d="M10 2.5 C6.5 2.5 4 5.2 4 8.5 C4 13 10 18 10 18 C10 18 16 13 16 8.5 C16 5.2 13.5 2.5 10 2.5Z" {...s} fill={active?"rgba(255,255,255,0.12)":undefined}/>
      </svg>);
    case "schools": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <path d="M2 18 V9 L10 3 L18 9 V18" {...s} fill={active?"rgba(255,255,255,0.1)":undefined}/>
        <rect x="7" y="12" width="6" height="6" rx="0.5" {...s}/>
        <path d="M10 3 L10 1.5" {...s}/>
        <rect x="8.5" y="1" width="3" height="1.5" rx="0.5" {...s}/>
      </svg>);
    case "admins": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <circle cx="10" cy="6.5" r="3" {...s}/>
        <path d="M4 17 C4 13.7 6.7 11 10 11 C13.3 11 16 13.7 16 17" {...s}/>
        <path d="M10 11 L10 14.5" stroke={c} strokeWidth="1.3"/>
        <polygon points="10,11 13,14 10,17 7,14" {...s} fill={active?"rgba(255,255,255,0.15)":undefined}/>
      </svg>);
    case "users": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <circle cx="7.5" cy="6" r="2.5" {...s}/>
        <circle cx="13.5" cy="6" r="2" {...s}/>
        <path d="M2 16.5 C2 13.5 4.7 11 7.5 11 C10.3 11 13 13.5 13 16.5" {...s}/>
        <path d="M13.5 11.5 C15.5 11.5 18 13.3 18 16" {...s}/>
      </svg>);
    case "finance": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <rect x="2" y="5" width="16" height="11" rx="2" {...s} fill={active?"rgba(255,255,255,0.08)":undefined}/>
        <path d="M2 9 L18 9" {...s}/>
        <circle cx="10" cy="12.5" r="1.5" {...s} fill={active?"rgba(255,255,255,0.2)":undefined}/>
        <path d="M6 12.5 L7.5 12.5 M12.5 12.5 L14 12.5" {...s}/>
        <path d="M10 2 L10 5 M7.5 2.5 L10 5 L12.5 2.5" {...s}/>
      </svg>);
    case "merchants": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <path d="M3 4 H17 L15.5 9.5 H4.5 Z" {...s} fill={active?"rgba(255,255,255,0.12)":undefined}/>
        <rect x="4" y="9.5" width="12" height="7.5" rx="1" {...s}/>
        <path d="M8 17 L8 13.5 Q8 12 10 12 Q12 12 12 13.5 L12 17" {...s}/>
        <path d="M3 4 L2 2" {...s}/>
      </svg>);
    case "billing": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <rect x="2.5" y="4" width="15" height="12" rx="1.5" {...s} fill={active?"rgba(255,255,255,0.08)":undefined}/>
        <path d="M2.5 8 L17.5 8" {...s}/>
        <path d="M10 4 L10 2 M7 4 L7 2 M13 4 L13 2" {...s}/>
        <path d="M5.5 11.5 L9 11.5 M11 11.5 L14.5 11.5 M5.5 13.5 L8 13.5" {...s}/>
      </svg>);
    case "modules": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <rect x="2" y="2" width="7" height="7" rx="1.5" {...s} fill={active?"rgba(255,255,255,0.12)":undefined}/>
        <rect x="11" y="2" width="7" height="7" rx="1.5" {...s} fill={active?"rgba(255,255,255,0.08)":undefined}/>
        <rect x="2" y="11" width="7" height="7" rx="1.5" {...s} fill={active?"rgba(255,255,255,0.08)":undefined}/>
        <path d="M11 14.5 L18 14.5 M14.5 11 L14.5 18" {...s}/>
      </svg>);
    case "transport": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <rect x="2" y="6" width="16" height="9" rx="2" {...s} fill={active?"rgba(255,255,255,0.1)":undefined}/>
        <path d="M2 10 L18 10" {...s}/>
        <circle cx="5.5" cy="16.5" r="1.5" {...s} fill={active?"rgba(255,255,255,0.2)":undefined}/>
        <circle cx="14.5" cy="16.5" r="1.5" {...s} fill={active?"rgba(255,255,255,0.2)":undefined}/>
        <path d="M7 6 L7 2.5 M13 6 L13 2.5 M7 2.5 L13 2.5" {...s}/>
        <path d="M8 13 L12 13" {...s}/>
      </svg>);
    case "boarding": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <path d="M3 16.5 V8 L10 3 L17 8 V16.5" {...s} fill={active?"rgba(255,255,255,0.08)":undefined}/>
        <path d="M7 16.5 V12 Q7 10 10 10 Q13 10 13 12 V16.5" {...s}/>
        <path d="M3 16.5 L17 16.5" {...s}/>
        <circle cx="10" cy="6.5" r="1" {...s} fill={c}/>
      </svg>);
    case "health": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <path d="M10 17 C10 17 3 12.5 3 8 C3 5.2 5.5 3 8.5 3 C9.5 3 10.4 3.4 11 4 L10 5 L9 4 C9.6 3.4 9.7 3 10 3 C12.8 3 17 5.2 17 8 C17 12.5 10 17 10 17Z" {...s} fill={active?"rgba(255,255,255,0.12)":undefined}/>
        <path d="M7 9 L9.5 9 L9.5 6.5 L10.5 6.5 L10.5 9 L13 9" {...s}/>
      </svg>);
    case "academics": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <path d="M10 2 L19 7 L10 12 L1 7 Z" {...s} fill={active?"rgba(255,255,255,0.12)":undefined}/>
        <path d="M5 9.5 V14 C5 14 7 17 10 17 C13 17 15 14 15 14 V9.5" {...s}/>
        <path d="M19 7 V12.5" {...s}/>
        <circle cx="19" cy="13" r="0.8" {...s} fill={c}/>
      </svg>);
    case "comms": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <path d="M3 3 H13 Q15 3 15 5 V11 Q15 13 13 13 H9 L5 17 V13 H3 Q1 13 1 11 V5 Q1 3 3 3Z" {...s} fill={active?"rgba(255,255,255,0.1)":undefined}/>
        <path d="M5 7 L11 7 M5 9.5 L9 9.5" {...s}/>
        <path d="M17 6 Q19 6 19 8 V12 Q19 14 17 14 H15.5 V16 L13 14 H12" {...s}/>
      </svg>);
    case "ai": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <circle cx="10" cy="10" r="7.5" {...s} fill={active?"rgba(255,255,255,0.08)":undefined}/>
        <circle cx="10" cy="10" r="2.5" {...s} fill={active?"rgba(255,255,255,0.18)":undefined}/>
        <path d="M10 2.5 L10 7.5 M10 12.5 L10 17.5 M2.5 10 L7.5 10 M12.5 10 L17.5 10" {...s} strokeWidth="1.4"/>
        <path d="M4.7 4.7 L7.6 7.6 M12.4 12.4 L15.3 15.3 M4.7 15.3 L7.6 12.4 M12.4 7.6 L15.3 4.7" {...s} strokeWidth="1.1"/>
      </svg>);
    case "compliance": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <path d="M10 2 L17 5 V10 C17 14.5 13.5 17.5 10 18.5 C6.5 17.5 3 14.5 3 10 V5 Z" {...s} fill={active?"rgba(255,255,255,0.1)":undefined}/>
        <path d="M7 10 L9 12.5 L14 7.5" {...s} strokeWidth="2"/>
      </svg>);
    case "security": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <rect x="5" y="9" width="10" height="8" rx="1.5" {...s} fill={active?"rgba(255,255,255,0.12)":undefined}/>
        <path d="M7.5 9 V6.5 A2.5 2.5 0 0 1 12.5 6.5 V9" {...s}/>
        <circle cx="10" cy="13" r="1.2" {...s} fill={active?"rgba(255,255,255,0.25)":undefined}/>
        <path d="M10 13 L10 15" {...s}/>
      </svg>);
    case "platform": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <rect x="2" y="4" width="16" height="4" rx="1" {...s} fill={active?"rgba(255,255,255,0.12)":undefined}/>
        <rect x="2" y="10" width="16" height="4" rx="1" {...s} fill={active?"rgba(255,255,255,0.08)":undefined}/>
        <circle cx="5.5" cy="6" r="0.8" {...s} fill={c}/>
        <circle cx="5.5" cy="12" r="0.8" {...s} fill={c}/>
        <path d="M8 6 L14 6 M8 12 L14 12" {...s}/>
        <path d="M4 16 L8 16 M10 16 L13 16" {...s}/>
      </svg>);
    case "bi": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <path d="M3 16 L3 12 L6.5 12 L6.5 16" {...s} fill={active?"rgba(255,255,255,0.2)":undefined}/>
        <path d="M8 16 L8 8 L11.5 8 L11.5 16" {...s} fill={active?"rgba(255,255,255,0.15)":undefined}/>
        <path d="M13 16 L13 5 L16.5 5 L16.5 16" {...s} fill={active?"rgba(255,255,255,0.1)":undefined}/>
        <path d="M2 16 L18 16" {...s} strokeWidth="1.8"/>
        <path d="M5 10 L9 7 L13 9.5 L17 4" {...s} strokeWidth="1.5"/>
        <circle cx="17" cy="4" r="1.2" {...s} fill={c}/>
      </svg>);
    case "branding": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <circle cx="10" cy="10" r="7.5" {...s} fill={active?"rgba(255,255,255,0.06)":undefined}/>
        <circle cx="10" cy="10" r="3" {...s} fill={active?"rgba(255,255,255,0.15)":undefined}/>
        <path d="M10 2.5 C10 2.5 12.5 5.5 12.5 10 C12.5 14.5 10 17.5 10 17.5" {...s}/>
        <path d="M10 2.5 C10 2.5 7.5 5.5 7.5 10 C7.5 14.5 10 17.5 10 17.5" {...s}/>
        <path d="M2.5 10 L17.5 10" {...s}/>
        <path d="M3.2 6.5 L16.8 6.5 M3.2 13.5 L16.8 13.5" {...s} strokeWidth="1.1"/>
      </svg>);
    case "tenant_analytics": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <circle cx="10" cy="10" r="7.5" {...s} fill={active?"rgba(255,255,255,0.07)":undefined}/>
        <path d="M10 10 L10 2.5" {...s} strokeWidth="2"/>
        <path d="M10 10 L16.5 13.5" {...s} strokeWidth="2"/>
        <path d="M10 10 L5 15" {...s} strokeWidth="1.5"/>
        <circle cx="10" cy="10" r="1.8" {...s} fill={c}/>
      </svg>);
    case "godmode": return (
      <svg viewBox="0 0 20 20" className={cls}>
        <polygon points="10,2 12.4,7.6 18.5,8.2 14.2,12.2 15.4,18.2 10,15.2 4.6,18.2 5.8,12.2 1.5,8.2 7.6,7.6" {...s} fill={active?"rgba(255,255,255,0.2)":undefined} strokeWidth="1.6"/>
        <circle cx="10" cy="10.5" r="2.2" {...s} fill={active?"rgba(255,255,255,0.3)":undefined}/>
      </svg>);
    default: return <LayoutDashboard className={`h-[18px] w-[18px] ${active ? "text-white" : "text-white/65"}`}/>;
  }
}

const NAV: { id: Section; label: string; group: string }[] = [
  { id: "exec",             label: "Executive",            group: "Overview" },
  { id: "geo",              label: "Geo Intelligence",     group: "Overview" },
  { id: "schools",          label: "Schools",              group: "Overview" },
  { id: "admins",           label: "School admins",        group: "Overview" },
  { id: "users",            label: "Users & roles",        group: "Overview" },
  { id: "finance",          label: "Finance",              group: "Money" },
  { id: "merchants",        label: "Merchants",            group: "Money" },
  { id: "billing",          label: "Subscriptions",        group: "Money" },
  { id: "modules",          label: "Modules & add-ons",    group: "Money" },
  { id: "transport",        label: "Mobility Network",     group: "Operations" },
  { id: "boarding",         label: "Boarding",             group: "Operations" },
  { id: "health",           label: "Health & Care",        group: "Operations" },
  { id: "academics",        label: "Academics",            group: "Operations" },
  { id: "comms",            label: "Communications",       group: "Engagement" },
  { id: "ai",               label: "AI copilot",           group: "Engagement" },
  { id: "compliance",       label: "Compliance & audit",   group: "Governance" },
  { id: "security",         label: "Security",             group: "Governance" },
  { id: "platform",         label: "Platform ops",         group: "Governance" },
  { id: "bi",               label: "Reports & BI",         group: "Governance" },
  { id: "branding",         label: "White-label branding", group: "Governance" },
  { id: "tenant_analytics", label: "Tenant analytics",     group: "Governance" },
  { id: "godmode",          label: "God mode",             group: "Governance" },
];

export function SuperadminDashboard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [section, setSection] = useState<Section>(() => (typeof window !== "undefined" ? (localStorage.getItem(SECTION_KEY) as Section) || "exec" : "exec"));
  const [viewAs, setViewAs] = useState<string>(() => (typeof window !== "undefined" ? localStorage.getItem(VIEW_AS_KEY) || "superadmin" : "superadmin"));
  const [mobileOpen, setMobileOpen] = useState(false);

  const setRole = (r: string) => { setViewAs(r); if (typeof window !== "undefined") localStorage.setItem(VIEW_AS_KEY, r); };
  const goto = (s: Section) => { setSection(s); if (typeof window !== "undefined") localStorage.setItem(SECTION_KEY, s); setMobileOpen(false); };

  if (viewAs === "parent")   return <ImpersonationFrame label="Viewing as Parent"   onExit={() => setRole("superadmin")}><ParentDashboard /></ImpersonationFrame>;
  if (viewAs === "student")  return <ImpersonationFrame label="Viewing as Student"  onExit={() => setRole("superadmin")}><StudentDashboard /></ImpersonationFrame>;
  if (viewAs === "merchant") return <ImpersonationFrame label="Viewing as Merchant" onExit={() => setRole("superadmin")}><MerchantDashboard /></ImpersonationFrame>;

  return (
    <div className="flex min-h-screen bg-[#080C14] -mx-4 -my-8">
      {/* ── Desktop sidebar ─────────────────────────────── */}
      <aside className="hidden lg:flex lg:flex-col lg:w-[220px] xl:w-[244px] lg:shrink-0 lg:sticky lg:top-0 lg:h-screen">
        <SidebarContent section={section} onNav={goto} viewAs={viewAs} setRole={setRole} />
      </aside>

      {/* ── Mobile sidebar Sheet ────────────────────────── */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-[260px] border-0 bg-transparent shadow-2xl">
          <SidebarContent section={section} onNav={goto} viewAs={viewAs} setRole={setRole} />
        </SheetContent>
      </Sheet>

      {/* ── Main ─────────────────────────────────────────── */}
      <div className="flex flex-1 flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-12 items-center gap-3 border-b border-white/[0.07] bg-[#080C14]/85 backdrop-blur-md px-4">
          <button
            className="lg:hidden flex items-center justify-center h-7 w-7 rounded-md border border-white/10 text-white/60 hover:text-white hover:bg-white/5 transition"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-3.5 w-3.5" />
          </button>
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className="text-[10px] text-white/30 font-mono uppercase tracking-widest hidden sm:inline">Platform Admin</span>
            <span className="text-white/20 hidden sm:inline">/</span>
            <span className="text-xs font-medium text-white/70 truncate">{NAV.find(n => n.id === section)?.label}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden sm:flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"/>
              <span className="text-[10px] font-semibold text-emerald-400 tracking-wide">LIVE</span>
            </div>
            <select
              value={viewAs}
              onChange={e => setRole(e.target.value)}
              className="hidden md:block rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-[11px] text-white/60 focus:outline-none cursor-pointer"
            >
              <option value="superadmin">Superadmin</option>
              <option value="school_admin">School admin</option>
              <option value="parent">Parent</option>
              <option value="student">Student</option>
              <option value="merchant">Merchant</option>
            </select>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 w-full max-w-[1600px] mx-auto px-3 py-4 sm:px-5 lg:px-6">
          <div className="rounded-2xl bg-background min-h-[calc(100vh-7rem)] overflow-hidden shadow-2xl ring-1 ring-black/[0.06]">
            <div className="border-b px-6 py-4 flex items-center gap-3 bg-muted/20">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" style={{ background: (GROUP_META[NAV.find(n=>n.id===section)?.group ?? "Overview"]?.accent ?? "#16a34a") + "20", color: GROUP_META[NAV.find(n=>n.id===section)?.group ?? "Overview"]?.accent ?? "#16a34a" }}>
                <NavIcon id={section} active={true} />
              </div>
              <div className="min-w-0">
                <p className="text-[9.5px] font-bold uppercase tracking-widest text-muted-foreground/60">{NAV.find(n=>n.id===section)?.group}</p>
                <p className="text-[13px] font-semibold text-foreground leading-tight truncate">{NAV.find(n=>n.id===section)?.label}</p>
              </div>
            </div>
            <div className="p-5 sm:p-6 lg:p-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={section}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                >
                  <ConsoleHome user={user} qc={qc} setRole={setRole} viewAs={viewAs} section={section} goto={goto} />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function ImpersonationFrame({ label, onExit, children }: { label: string; onExit: () => void; children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between rounded-xl border border-[var(--terracotta)]/40 bg-[color-mix(in_oklab,var(--terracotta)_8%,transparent)] px-4 py-2 text-sm">
        <div className="flex items-center gap-2"><Eye className="h-4 w-4 text-[var(--terracotta)]" /><span className="font-medium">{label}</span><Badge variant="secondary">admin impersonation · audited</Badge></div>
        <Button size="sm" variant="outline" onClick={onExit}>Exit view-as</Button>
      </motion.div>
      {children}
    </div>
  );
}

// ── Collapsible sidebar ───────────────────────────────────────────────────────
const GROUP_META: Record<string, { icon: React.ReactNode; accent: string }> = {
  Overview:    { icon: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><rect x="1" y="1" width="5.5" height="5.5" rx="1"/><rect x="9.5" y="1" width="5.5" height="5.5" rx="1"/><rect x="1" y="9.5" width="5.5" height="5.5" rx="1"/><rect x="9.5" y="9.5" width="5.5" height="5.5" rx="1"/></svg>, accent: "#22c55e" },
  Money:       { icon: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><rect x="1" y="4" width="14" height="9" rx="1.5"/><path d="M1 7h14"/><circle cx="8" cy="10.5" r="1.3"/></svg>, accent: "#f59e0b" },
  Operations:  { icon: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><path d="M8 1l7 4-7 5L1 5z"/><path d="M4 7.5v3s1.5 2 4 2 4-2 4-2v-3"/></svg>, accent: "#3b82f6" },
  Engagement:  { icon: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><path d="M2 2h8q1.5 0 1.5 1.5v5q0 1.5-1.5 1.5H6l-3 3V9H2Q.5 9 .5 7.5v-5Q.5 2 2 2z"/><path d="M13 4.5q2 0 2 1.5v4q0 1.5-2 1.5h-1v2.5L9.5 11"/></svg>, accent: "#a855f7" },
  Governance:  { icon: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5"><path d="M8 1l5.5 2.5v4C13.5 11.5 11 14 8 15 5 14 2.5 11.5 2.5 7.5v-4z"/><path d="M5.5 8l2 2.5L11 6"/></svg>, accent: "#ef4444" },
};

function SidebarContent({ section, onNav, viewAs, setRole }: { section: Section; onNav: (id: Section) => void; viewAs: string; setRole: (r: string) => void }) {
  const groups = useMemo(() => {
    const g: Record<string, typeof NAV> = {};
    for (const n of NAV) { (g[n.group] ??= []).push(n); }
    return g;
  }, []);

  // Start with the active item's group open, all others closed
  const activeGroup = useMemo(() => NAV.find(n => n.id === section)?.group ?? "Overview", [section]);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const g of Object.keys(GROUP_META)) init[g] = g === activeGroup;
    return init;
  });

  const toggle = (g: string) => setOpenGroups(prev => ({ ...prev, [g]: !prev[g] }));

  // Sync open state when active section changes
  useMemo(() => {
    setOpenGroups(prev => ({ ...prev, [activeGroup]: true }));
  }, [activeGroup]);

  return (
    <div
      className="flex flex-col h-full"
      style={{
        background: "linear-gradient(180deg, #0c1220 0%, #090e1a 60%, #070b16 100%)",
        borderRight: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      {/* ── Brand ── */}
      <div className="flex items-center gap-3 px-4 py-4 border-b border-white/[0.05] shrink-0">
        <div className="relative h-9 w-9 shrink-0">
          <div className="absolute inset-0 rounded-[11px]" style={{ background: "linear-gradient(135deg,#16a34a,#052e16)" }} />
          <div className="absolute inset-[1.5px] rounded-[9.5px]" style={{ background: "linear-gradient(135deg,rgba(255,255,255,0.1),rgba(255,255,255,0))" }} />
          <svg viewBox="0 0 24 24" className="absolute inset-0 m-auto h-4.5 w-4.5" fill="none" stroke="rgba(255,255,255,0.95)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3L21 8L12 13L3 8Z" fill="rgba(255,255,255,0.1)" />
            <path d="M7 10.5V15C9 17 15 17 17 15V10.5" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-bold text-white tracking-tight truncate">SchoolOS</p>
          <p className="text-[10px] text-white/30 truncate font-mono">platform · console</p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="rounded-md bg-amber-400/20 border border-amber-400/30 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-amber-400">God</span>
        </div>
      </div>

      {/* ── Nav ── */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
        {Object.entries(groups).map(([group, items]) => {
          const meta = GROUP_META[group] ?? GROUP_META.Overview;
          const isOpen = openGroups[group] ?? false;
          const hasActive = items.some(i => i.id === section);

          return (
            <div key={group}>
              {/* Group header — collapsible */}
              <button
                onClick={() => toggle(group)}
                className={`w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 mb-0.5 transition-all duration-150 group ${
                  hasActive ? "text-white" : "text-white/40 hover:text-white/70"
                }`}
                style={hasActive ? { background: "rgba(255,255,255,0.04)" } : undefined}
              >
                <span
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md transition-colors"
                  style={{ color: isOpen || hasActive ? meta.accent : "rgba(255,255,255,0.3)", background: isOpen || hasActive ? `${meta.accent}18` : "rgba(255,255,255,0.04)" }}
                >
                  {meta.icon}
                </span>
                <span className="flex-1 text-left text-[11px] font-semibold uppercase tracking-[0.1em]">{group}</span>
                <span className="flex items-center gap-1.5">
                  {hasActive && <span className="h-1.5 w-1.5 rounded-full" style={{ background: meta.accent }} />}
                  <svg
                    viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8"
                    className={`h-3 w-3 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}
                  >
                    <path d="M4 2l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </button>

              {/* Sub-items with animated expand */}
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
                    style={{ overflow: "hidden" }}
                  >
                    <div className="ml-2 pl-2 border-l border-white/[0.07] space-y-px pb-2">
                      {items.map(item => {
                        const active = section === item.id;
                        return (
                          <button
                            key={item.id}
                            onClick={() => onNav(item.id)}
                            className={`group w-full flex items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-left text-[12px] font-medium transition-all duration-150 relative ${
                              active ? "text-white" : "text-white/45 hover:text-white/80 hover:bg-white/[0.04]"
                            }`}
                            style={active ? {
                              background: `linear-gradient(90deg,${meta.accent}28 0%,${meta.accent}0a 100%)`,
                              boxShadow: `inset 0 0 0 1px ${meta.accent}35`,
                            } : undefined}
                          >
                            {active && (
                              <span
                                className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 rounded-full"
                                style={{ background: meta.accent }}
                              />
                            )}
                            <NavIcon id={item.id} active={active} />
                            <span className="truncate flex-1">{item.label}</span>
                            {active && (
                              <svg viewBox="0 0 8 8" fill="currentColor" className="h-2 w-2 shrink-0 opacity-40">
                                <circle cx="4" cy="4" r="3" />
                              </svg>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </nav>

      {/* ── Footer ── */}
      <div className="shrink-0 border-t border-white/[0.05] px-3 py-3 space-y-2">
        {/* View-as */}
        <div className="rounded-lg border border-white/[0.07] bg-white/[0.03] p-2">
          <p className="text-[9px] font-semibold uppercase tracking-widest text-white/25 mb-1.5 px-1">View as role</p>
          <div className="grid grid-cols-2 gap-1">
            {(["superadmin","school_admin","parent","student","merchant"] as const).map(r => (
              <button
                key={r}
                onClick={() => setRole(r)}
                className={`rounded-md px-2 py-1 text-[10px] font-medium transition capitalize ${
                  viewAs === r
                    ? "bg-white/15 text-white"
                    : "text-white/35 hover:text-white/60 hover:bg-white/[0.05]"
                }`}
              >
                {r === "school_admin" ? "School" : r === "superadmin" ? "Admin" : r}
              </button>
            ))}
          </div>
        </div>

        {/* User info */}
        <div className="flex items-center gap-2.5 rounded-lg bg-white/[0.03] border border-white/[0.05] px-2.5 py-2">
          <div
            className="h-6 w-6 rounded-full shrink-0 flex items-center justify-center text-[9px] font-bold text-white"
            style={{ background: "linear-gradient(135deg,#16a34a,#166534)" }}
          >SA</div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold text-white/80 truncate">Platform Admin</p>
            <p className="text-[9.5px] text-white/30 truncate">superadmin@demo.com</p>
          </div>
          <div className="h-1.5 w-1.5 rounded-full bg-emerald-400 shrink-0" />
        </div>
      </div>
    </div>
  );
}

function ConsoleHome({ user, qc, setRole, viewAs, section, goto }: { user: any; qc: ReturnType<typeof useQueryClient>; setRole: (r: string) => void; viewAs: string; section: Section; goto: (s: Section) => void }) {
  const { data: schools = [] } = useQuery({
    queryKey: ["all-schools"],
    queryFn: async () => (await supabase.from("schools").select("*").order("name")).data ?? [],
  });

  const { data: stats } = useQuery({
    queryKey: ["admin-stats"],
    refetchInterval: 5000,
    queryFn: async () => {
      const [sch, mer, wal, txs, stu, prf, rls] = await Promise.all([
        supabase.from("schools").select("id, active, student_count, staff_count", { count: "exact" }),
        supabase.from("merchants").select("id", { count: "exact", head: true }),
        supabase.from("wallets").select("balance_ugx"),
        supabase.from("transactions").select("amount_ugx, fee_ugx, type, status, created_at").order("created_at", { ascending: false }).limit(500),
        supabase.from("students").select("id", { count: "exact", head: true }),
        supabase.from("profiles").select("id", { count: "exact", head: true }),
        supabase.from("user_roles").select("role"),
      ]);
      const totalBalance = (wal.data ?? []).reduce((a, w) => a + (w.balance_ugx ?? 0), 0);
      const totalVolume = (txs.data ?? []).reduce((a, t) => a + (t.amount_ugx ?? 0), 0);
      const totalFees   = (txs.data ?? []).reduce((a, t) => a + (t.fee_ugx ?? 0), 0);
      const failedTx    = (txs.data ?? []).filter((t) => t.status === "failed").length;
      const activeSchools = (sch.data ?? []).filter((s) => s.active).length;
      const totalStudentsReg = (sch.data ?? []).reduce((a, s) => a + (s.student_count ?? 0), 0);
      const totalStaff = (sch.data ?? []).reduce((a, s) => a + (s.staff_count ?? 0), 0);
      const parents   = (rls.data ?? []).filter((r) => r.role === "parent").length;
      const teachers  = (rls.data ?? []).filter((r) => r.role === "school_admin").length;
      return {
        schools: sch.count ?? 0, activeSchools, merchants: mer.count ?? 0,
        totalBalance, totalVolume, totalFees, failedTx,
        wallets: wal.data?.length ?? 0, students: stu.count ?? 0, totalStudentsReg, totalStaff,
        users: prf.count ?? 0, parents, teachers,
        txCount: txs.data?.length ?? 0,
      };
    },
  });

  const { data: settlements = [] } = useQuery({
    queryKey: ["settlements"],
    queryFn: async () => (await supabase.from("settlements").select("*").order("created_at", { ascending: false }).limit(20)).data ?? [],
  });

  const { data: recentTx = [] } = useQuery({
    queryKey: ["recent-tx-all"],
    refetchInterval: 3000,
    queryFn: async () => (await supabase.from("transactions").select("*").order("created_at", { ascending: false }).limit(30)).data ?? [],
  });

  const { data: audit = [] } = useQuery({
    queryKey: ["audit"],
    refetchInterval: 2500,
    queryFn: async () => (await supabase.from("audit_logs").select("*").order("created_at", { ascending: false }).limit(80)).data ?? [],
  });

  // ---------- Settlement helpers (preserved from previous build) ----------
  const FEE_SPLIT = { txn: 120, settlement: 50, software: 30 };
  const runSettlement = useMutation({
    mutationFn: async () => {
      const { data: merchants } = await supabase.from("merchants").select("id, name");
      const today = new Date().toISOString().slice(0, 10);
      let created = 0;
      for (const m of merchants ?? []) {
        const { data: txs } = await supabase.from("transactions").select("amount_ugx").eq("merchant_id", m.id).eq("type", "purchase").eq("status", "posted").gte("created_at", `${today}T00:00:00Z`);
        if (!txs || txs.length === 0) continue;
        const gross = txs.reduce((a, t) => a + t.amount_ugx, 0);
        const txn = Math.round((gross * FEE_SPLIT.txn) / 10000);
        const stl = Math.round((gross * FEE_SPLIT.settlement) / 10000);
        const swf = Math.round((gross * FEE_SPLIT.software) / 10000);
        const fee = txn + stl + swf;
        const ref = `STL-${today.replace(/-/g, "")}-${m.id.slice(0, 6).toUpperCase()}`;
        await supabase.from("settlements").insert({
          merchant_id: m.id, gross_ugx: gross, fee_ugx: fee, net_ugx: gross - fee,
          txn_fee_ugx: txn, settlement_fee_ugx: stl, software_fee_ugx: swf,
          tx_count: txs.length, status: "queued", batch_date: today, reference: ref,
        });
        created++;
      }
      await supabase.from("audit_logs").insert({ actor_id: user!.id, action: "settlement.batch_run", metadata: { date: today, batches: created } });
      if (created === 0) throw new Error("No posted purchases today to settle");
    },
    onSuccess: () => { toast.success("Settlement batch queued"); qc.invalidateQueries({ queryKey: ["settlements"] }); qc.invalidateQueries({ queryKey: ["audit"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const downloadRecon = async (s: any) => {
    const { data: merchant } = await supabase.from("merchants").select("name").eq("id", s.merchant_id).maybeSingle();
    const { data: txs } = await supabase.from("transactions").select("id, amount_ugx, fee_ugx, description, reference, created_at").eq("merchant_id", s.merchant_id).eq("type", "purchase").eq("status", "posted").gte("created_at", `${s.batch_date}T00:00:00Z`).lte("created_at", `${s.batch_date}T23:59:59Z`).order("created_at");
    const rows = txs ?? [];
    const head = ["batch_ref", "batch_date", "merchant", "tx_id", "tx_time", "tx_ref", "description", "amount_ugx", "txn_fee_share_ugx", "settlement_fee_share_ugx", "software_fee_share_ugx", "merchant_net_ugx"];
    const lines = [head.join(",")];
    for (const t of rows) {
      const share = s.gross_ugx ? t.amount_ugx / s.gross_ugx : 0;
      const tShare = Math.round(s.txn_fee_ugx * share);
      const sShare = Math.round(s.settlement_fee_ugx * share);
      const wShare = Math.round(s.software_fee_ugx * share);
      const net = t.amount_ugx - tShare - sShare - wShare;
      lines.push([s.reference ?? s.id.slice(0,8), s.batch_date, (merchant?.name ?? "").replace(/,/g, " "), t.id, t.created_at, t.reference ?? "", (t.description ?? "").replace(/,/g, " "), t.amount_ugx, tShare, sShare, wShare, net].join(","));
    }
    lines.push("");
    lines.push(["TOTALS", s.batch_date, merchant?.name ?? "", "", "", "", "", s.gross_ugx, s.txn_fee_ugx, s.settlement_fee_ugx, s.software_fee_ugx, s.net_ugx].join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `recon-${s.reference ?? s.id.slice(0,8)}.csv`; a.click();
    await supabase.from("audit_logs").insert({ actor_id: user!.id, action: "settlement.recon_exported", entity_type: "settlement", entity_id: s.id, metadata: { rows: rows.length, ref: s.reference } });
    toast.success(`Reconciliation exported · ${rows.length} transactions`);
  };

  const markSettled = async (s: any) => {
    await supabase.from("settlements").update({ status: "settled" }).eq("id", s.id);
    await supabase.from("audit_logs").insert({ actor_id: user!.id, action: "settlement.marked_settled", entity_type: "settlement", entity_id: s.id, metadata: { ref: s.reference } });
    qc.invalidateQueries({ queryKey: ["settlements"] }); qc.invalidateQueries({ queryKey: ["audit"] });
    toast.success("Marked settled");
  };

  const exportAudit = async (opts: { from?: string; to?: string; action?: string; format: "csv" | "pdf"; label: string }) => {
    let q = supabase.from("audit_logs").select("*").order("created_at", { ascending: false });
    if (opts.from)   q = q.gte("created_at", new Date(opts.from).toISOString());
    if (opts.to)     q = q.lte("created_at", new Date(opts.to + "T23:59:59").toISOString());
    if (opts.action && opts.action !== "__all") q = q.like("action", `${opts.action}%`);
    const { data } = await q;
    const rows = data ?? [];
    const stamp = new Date().toISOString().slice(0, 10);
    const baseName = `audit-${opts.label}-${stamp}`;
    if (opts.format === "csv") {
      const csv = ["id,actor_id,action,entity_type,entity_id,created_at,metadata", ...rows.map((a) => `${a.id},${a.actor_id},${a.action},${a.entity_type ?? ""},${a.entity_id ?? ""},${a.created_at},"${JSON.stringify(a.metadata ?? {}).replace(/"/g, '""')}"`)].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${baseName}.csv`; a.click();
    } else {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageH = doc.internal.pageSize.getHeight();
      doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.text("TriSphere Uganda · Audit Archive", 40, 50);
      doc.setFont("helvetica", "normal"); doc.setFontSize(9);
      doc.text(`Generated ${new Date().toLocaleString("en-UG")}`, 40, 66);
      doc.text(`Filters · ${opts.label} · ${opts.action ?? "all actions"} · ${rows.length} records`, 40, 80);
      doc.text("Retained 10 years per Uganda PSA 2020. Append-only.", 40, 94);
      let y = 120; doc.setFontSize(8); doc.setFont("helvetica", "bold");
      doc.text("When", 40, y); doc.text("Action", 160, y); doc.text("Entity", 340, y); doc.text("Actor", 460, y); y += 12;
      doc.setFont("helvetica", "normal");
      for (const a of rows) {
        if (y > pageH - 40) { doc.addPage(); y = 40; }
        doc.text(shortDateTime(a.created_at), 40, y);
        doc.text((a.action ?? "").slice(0, 32), 160, y);
        doc.text(`${a.entity_type ?? "-"} ${a.entity_id ? a.entity_id.slice(0,8) : ""}`, 340, y);
        doc.text((a.actor_id ?? "-").slice(0, 12), 460, y);
        y += 11;
      }
      doc.save(`${baseName}.pdf`);
    }
    await supabase.from("audit_logs").insert({ actor_id: user!.id, action: "audit.export", metadata: { from: opts.from, to: opts.to, action: opts.action, format: opts.format, rows: rows.length, label: opts.label } });
    toast.success(`Exported ${rows.length} records as ${opts.format.toUpperCase()}`);
  };

  // ---------- Section routing ----------
  return (
    <>
      {section === "exec"       && <ExecSection stats={stats} schools={schools as any} audit={audit} onGoto={goto} />}
      {section === "geo"        && <GeoIntelligence />}
      {section === "schools"    && <SchoolsCommandCenter />}
      {section === "admins"     && <SuperadminSchoolAdminsConsole />}
      {section === "users"      && <SuperadminCRM initialTab="users" />}
      {section === "finance"    && <FinanceCommandCenter />}
      {section === "merchants"  && <MerchantsCommandCenter />}
      {section === "billing"    && <SuperadminCRM initialTab="subscribers" />}
      {section === "transport"  && <TransportCommandCenter />}
      {section === "boarding"   && <BoardingSection />}
      {section === "health"     && <HealthSection />}
      {section === "academics"  && <AcademicsSection />}
      {section === "comms"      && <CommsSection />}
      {section === "ai"         && <AISection audit={audit} />}
      {section === "compliance" && <ComplianceSection audit={audit} stats={stats} exportAudit={exportAudit} />}
      {section === "security"   && <SecuritySection audit={audit} />}
      {section === "platform"   && <PlatformSection stats={stats} />}
      {section === "bi"         && <BISection schools={schools as any} stats={stats} />}
      {section === "modules"    && <FeatureModulesConsole />}
      {section === "branding"   && <TenantBrandingConsole />}
      {section === "tenant_analytics" && <TenantAnalyticsConsole />}
      {section === "godmode"    && <GodModeSection setRole={setRole} />}
    </>
  );
}

// ============================================================
// Executive dashboard
// ============================================================
// ── Shared design primitives ──────────────────────────────────────────────────

function SectionHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6">
      <div>
        <h1 className="text-xl font-bold tracking-tight text-foreground">{title}</h1>
        {subtitle && <p className="text-[13px] text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

// Stat tile — gradient top-border card with micro-chart hint
function StatTileNew({ icon: Icon, label, value, sub, trend, accent = "#16a34a" }: {
  icon: any; label: string; value: string; sub?: string; trend?: number; accent?: string;
}) {
  return (
    <div
      className="relative rounded-2xl border bg-card p-5 overflow-hidden group hover:shadow-md transition-shadow"
      style={{ borderColor: "hsl(var(--border))" }}
    >
      <div className="absolute top-0 left-0 right-0 h-[2px] rounded-t-2xl" style={{ background: accent }} />
      <div className="flex items-start justify-between gap-3">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
          style={{ background: `${accent}18`, color: accent }}
        >
          <Icon className="h-4 w-4" />
        </div>
        {trend !== undefined && (
          <span
            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${trend >= 0 ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400" : "bg-red-50 text-red-600 dark:bg-red-950/50 dark:text-red-400"}`}
          >
            {trend >= 0 ? "+" : ""}{trend}%
          </span>
        )}
      </div>
      <p className="mt-3 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70">{label}</p>
      <p className="text-[26px] font-bold tracking-tight leading-none mt-1 text-foreground">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

// Premium table wrapper
function DataTable({ headers, children, empty }: { headers: string[]; children: React.ReactNode; empty?: boolean }) {
  return (
    <div className="rounded-2xl border bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30">
              {headers.map((h, i) => (
                <th
                  key={i}
                  className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70 whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50">
            {empty
              ? <tr><td colSpan={headers.length} className="px-4 py-12 text-center text-sm text-muted-foreground">No records found.</td></tr>
              : children}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TRow({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <tr
      className={`transition-colors hover:bg-muted/30 ${onClick ? "cursor-pointer" : ""}`}
      onClick={onClick}
    >
      {children}
    </tr>
  );
}
function TD({ children, className = "" }: { children?: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 text-sm align-middle ${className}`}>{children}</td>;
}

// Status pill
function StatusPill({ status }: { status: "active" | "suspended" | "pending" | "healthy" | "degraded" | "offline" | string }) {
  const map: Record<string, { bg: string; text: string; dot: string }> = {
    active:    { bg: "bg-emerald-50 dark:bg-emerald-950/40",  text: "text-emerald-700 dark:text-emerald-400",  dot: "bg-emerald-500" },
    healthy:   { bg: "bg-emerald-50 dark:bg-emerald-950/40",  text: "text-emerald-700 dark:text-emerald-400",  dot: "bg-emerald-500" },
    pending:   { bg: "bg-amber-50 dark:bg-amber-950/40",      text: "text-amber-700 dark:text-amber-400",      dot: "bg-amber-400" },
    suspended: { bg: "bg-red-50 dark:bg-red-950/40",          text: "text-red-700 dark:text-red-400",          dot: "bg-red-500" },
    offline:   { bg: "bg-red-50 dark:bg-red-950/40",          text: "text-red-700 dark:text-red-400",          dot: "bg-red-500" },
    degraded:  { bg: "bg-orange-50 dark:bg-orange-950/40",    text: "text-orange-700 dark:text-orange-400",    dot: "bg-orange-400" },
  };
  const s = map[status] ?? map.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${s.bg} ${s.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {status}
    </span>
  );
}

// Plan badge
function PlanBadge({ plan }: { plan: string }) {
  const map: Record<string, string> = {
    enterprise: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/40 dark:text-violet-400 dark:border-violet-800",
    standard:   "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-800",
    starter:    "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:border-slate-700",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold capitalize ${map[plan] ?? map.starter}`}>
      {plan}
    </span>
  );
}

// HealthBar (thin, colorful)
function HealthBar({ value }: { value: number }) {
  const color = value >= 80 ? "#16a34a" : value >= 50 ? "#f59e0b" : "#ef4444";
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${value}%`, background: color }} />
      </div>
      <span className="text-[11px] font-mono text-muted-foreground w-7 text-right">{value}</span>
    </div>
  );
}

// ── Exec section ──────────────────────────────────────────────────────────────
function ExecSection({ stats, schools, audit, onGoto }: { stats: any; schools: SchoolPin[]; audit: any[]; onGoto: (s: Section) => void }) {
  const kpis = [
    { icon: Building2,     label: "Total schools",     value: fmtNum(stats?.schools),      sub: `${stats?.activeSchools ?? 0} active`, accent: "#16a34a", trend: 4 },
    { icon: GraduationCap, label: "Students",           value: fmtNum(stats?.totalStudentsReg ?? stats?.students), sub: "enrolled", accent: "#3b82f6", trend: 8 },
    { icon: Users,         label: "Parents",            value: fmtNum(stats?.parents),      sub: `${fmtNum(stats?.users)} total users`, accent: "#8b5cf6", trend: 12 },
    { icon: Store,         label: "Merchants",          value: fmtNum(stats?.merchants),    sub: "active POS", accent: "#f59e0b", trend: 2 },
    { icon: Receipt,       label: "Float in custody",   value: fmtUGX(stats?.totalBalance), sub: "safeguarded · 1:1", accent: "#0ea5e9" },
    { icon: TrendingUp,    label: "Volume (last 500)",  value: fmtUGX(stats?.totalVolume),  sub: `${stats?.txCount ?? 0} transactions`, accent: "#10b981", trend: 15 },
    { icon: Banknote,      label: "Platform fees",      value: fmtUGX(stats?.totalFees),    sub: "2.00% blended", accent: "#6366f1" },
    { icon: AlertTriangle, label: "Failed tx",          value: fmtNum(stats?.failedTx ?? 0), sub: "needs review", accent: stats?.failedTx ? "#ef4444" : "#16a34a" },
  ];

  return (
    <div className="space-y-7">
      <SectionHeader
        title="Executive Overview"
        subtitle="Real-time platform health across all schools and tenants."
        action={
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onGoto("schools")}>
            <Building2 className="h-3.5 w-3.5" /> School registry
          </Button>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map(k => <StatTileNew key={k.label} {...k} />)}
      </div>

      <div className="rounded-2xl border bg-card overflow-hidden">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <p className="font-semibold text-foreground">Regional adoption map</p>
            <p className="text-[12px] text-muted-foreground mt-0.5">All schools across Uganda. Marker size = students · colour = plan.</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => onGoto("geo")}>Full map</Button>
        </div>
        <div className="p-4">
          <SchoolsMap schools={schools as any} height={400} />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        {/* Activity stream */}
        <div className="rounded-2xl border bg-card overflow-hidden">
          <div className="border-b px-5 py-4">
            <p className="font-semibold text-foreground">Live activity stream</p>
            <p className="text-[12px] text-muted-foreground mt-0.5">Most recent platform events</p>
          </div>
          <div className="divide-y max-h-72 overflow-y-auto">
            {audit.slice(0, 14).map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-muted/30 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
                  <span className="text-[12px] font-mono text-foreground/80 truncate">{a.action}</span>
                </div>
                <span className="text-[11px] text-muted-foreground shrink-0">{shortDateTime(a.created_at)}</span>
              </div>
            ))}
            {audit.length === 0 && <div className="px-5 py-10 text-center text-sm text-muted-foreground">No events yet.</div>}
          </div>
        </div>

        {/* Plan distribution */}
        <div className="rounded-2xl border bg-card p-5">
          <p className="font-semibold text-foreground mb-1">Plan distribution</p>
          <p className="text-[12px] text-muted-foreground mb-5">Subscription mix across schools</p>
          <div className="space-y-5">
            {[
              { plan: "enterprise", color: "#8b5cf6", label: "Enterprise" },
              { plan: "standard",   color: "#3b82f6", label: "Standard" },
              { plan: "starter",    color: "#6b7280", label: "Starter" },
            ].map(({ plan, color, label }) => {
              const n = (schools as any[]).filter(s => s.subscription_plan === plan).length;
              const pct = schools.length ? Math.round((n / schools.length) * 100) : 0;
              return (
                <div key={plan}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
                      <span className="text-[13px] font-medium">{label}</span>
                    </div>
                    <span className="text-[12px] text-muted-foreground">{n} schools · {pct}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: color }}
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.7, ease: "easeOut" }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 pt-4 border-t grid grid-cols-3 gap-3 text-center">
            <div>
              <p className="text-lg font-bold text-foreground">{schools.length}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Total</p>
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">{(schools as any[]).filter(s => s.active).length}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Active</p>
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">{(schools as any[]).filter(s => !s.active).length}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Suspended</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Schools registry ──────────────────────────────────────────────────────────
function SchoolsSection({ schools }: { schools: any[] }) {
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("__all");
  const [plan, setPlan] = useState("__all");
  const regions = useMemo(() => Array.from(new Set(schools.map(s => s.region).filter(Boolean))).sort() as string[], [schools]);
  const filtered = useMemo(() =>
    schools.filter(s =>
      (!q || s.name.toLowerCase().includes(q.toLowerCase()) || (s.district ?? "").toLowerCase().includes(q.toLowerCase()))
      && (region === "__all" || s.region === region)
      && (plan === "__all" || s.subscription_plan === plan),
    ), [schools, q, region, plan]);

  return (
    <div className="space-y-5">
      <SectionHeader
        title="School Registry"
        subtitle={`${schools.length} schools across Uganda`}
        action={<Button size="sm" className="gap-1.5 bg-[var(--forest)] hover:opacity-90 text-white"><Building2 className="h-3.5 w-3.5" /> Add school</Button>}
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or district…" className="pl-9 h-9 text-sm" />
        </div>
        <Select value={region} onValueChange={setRegion}>
          <SelectTrigger className="h-9 w-36 text-sm"><SelectValue placeholder="Region" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All regions</SelectItem>
            {regions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={plan} onValueChange={setPlan}>
          <SelectTrigger className="h-9 w-36 text-sm"><SelectValue placeholder="Plan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All plans</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
            <SelectItem value="standard">Standard</SelectItem>
            <SelectItem value="starter">Starter</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-[12px] text-muted-foreground ml-auto shrink-0">{filtered.length} of {schools.length}</span>
      </div>

      {/* Map */}
      <div className="rounded-2xl border bg-card overflow-hidden">
        <div className="border-b px-5 py-3 flex items-center justify-between">
          <p className="font-semibold text-[13px]">Map · {filtered.length} schools</p>
        </div>
        <div className="p-4"><SchoolsMap schools={filtered} height={400} /></div>
      </div>

      {/* Table */}
      <DataTable
        headers={["School", "Location", "Plan", "Students", "Staff", "Status", "Health"]}
        empty={filtered.length === 0}
      >
        {filtered.map(s => (
          <TRow key={s.id}>
            <TD>
              <div className="font-semibold text-foreground leading-tight">{s.name}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{s.registration_no ?? "—"} · TIN {s.ura_tin ?? "—"}</div>
            </TD>
            <TD>
              <div className="text-[13px]">{s.district ?? "—"}</div>
              <div className="text-[11px] text-muted-foreground">{s.region ?? "—"}</div>
            </TD>
            <TD><PlanBadge plan={s.subscription_plan} /></TD>
            <TD className="font-mono text-[13px]">{fmtNum(s.student_count)}</TD>
            <TD className="font-mono text-[13px]">{fmtNum(s.staff_count)}</TD>
            <TD><StatusPill status={s.active ? "active" : "suspended"} /></TD>
            <TD><HealthBar value={s.health_score ?? 0} /></TD>
          </TRow>
        ))}
      </DataTable>
    </div>
  );
}

// ── Users & roles ─────────────────────────────────────────────────────────────
function UsersSection({ stats }: { stats: any }) {
  const roles = [
    { name: "Super Admin",       count: 2,                              perms: "Full platform access",     tier: "god" },
    { name: "School Owner",      count: stats?.activeSchools ?? 0,      perms: "School-wide R/W",          tier: "high" },
    { name: "Head Teacher",      count: stats?.activeSchools ?? 0,      perms: "Academics + communications", tier: "high" },
    { name: "Bursar",            count: (stats?.activeSchools ?? 0)*2,  perms: "Finance read/write",       tier: "mid" },
    { name: "Accountant",        count: (stats?.activeSchools ?? 0)*2,  perms: "Finance read-only",        tier: "mid" },
    { name: "Welfare Officer",   count: stats?.activeSchools ?? 0,      perms: "Boarding + health",        tier: "mid" },
    { name: "Transport Manager", count: stats?.activeSchools ?? 0,      perms: "Fleet + routes",           tier: "mid" },
    { name: "Dormitory Master",  count: (stats?.activeSchools ?? 0)*3,  perms: "Boarding only",            tier: "low" },
    { name: "Teacher",           count: stats?.totalStaff ?? 0,         perms: "Class scope",              tier: "low" },
    { name: "Parent",            count: stats?.parents ?? 0,            perms: "Own children only",        tier: "user" },
    { name: "Merchant",          count: stats?.merchants ?? 0,          perms: "POS + wallet",             tier: "user" },
  ];
  const tierColor: Record<string, string> = {
    god: "#f59e0b", high: "#8b5cf6", mid: "#3b82f6", low: "#6b7280", user: "#10b981",
  };
  return (
    <div className="space-y-5">
      <SectionHeader title="Users & Roles" subtitle="Permission engine and user distribution." />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTileNew icon={Users}         label="Total users" value={fmtNum(stats?.users)}    accent="#8b5cf6" />
        <StatTileNew icon={GraduationCap} label="Students"    value={fmtNum(stats?.totalStudentsReg ?? stats?.students)} accent="#3b82f6" />
        <StatTileNew icon={Users}         label="Parents"     value={fmtNum(stats?.parents)}  accent="#10b981" />
        <StatTileNew icon={Store}         label="Merchants"   value={fmtNum(stats?.merchants)} accent="#f59e0b" />
      </div>
      <DataTable headers={["Role", "Tier", "Users", "Permissions", "Actions"]}>
        {roles.map(r => (
          <TRow key={r.name}>
            <TD><span className="font-semibold text-foreground">{r.name}</span></TD>
            <TD>
              <span
                className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                style={{ background: `${tierColor[r.tier]}20`, color: tierColor[r.tier] }}
              >
                {r.tier}
              </span>
            </TD>
            <TD className="font-mono text-[13px]">{fmtNum(r.count)}</TD>
            <TD className="text-muted-foreground text-[12px]">{r.perms}</TD>
            <TD>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="h-7 text-[11px]">View</Button>
                <Button size="sm" variant="ghost" className="h-7 text-[11px]">Edit</Button>
              </div>
            </TD>
          </TRow>
        ))}
      </DataTable>
    </div>
  );
}

// ── Finance ───────────────────────────────────────────────────────────────────
function FinanceSection({ stats, settlements, recentTx, runSettlement, downloadRecon, markSettled }: any) {
  return (
    <div className="space-y-5">
      <SectionHeader title="Finance Command Centre" subtitle="Float custody, settlements, and transaction ledger." />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTileNew icon={Banknote}      label="Fees collected"  value={fmtUGX(stats?.totalFees)}    accent="#16a34a" trend={5} />
        <StatTileNew icon={Receipt}       label="Float in custody" value={fmtUGX(stats?.totalBalance)} accent="#0ea5e9" />
        <StatTileNew icon={TrendingUp}    label="Volume (500 tx)" value={fmtUGX(stats?.totalVolume)}  accent="#8b5cf6" trend={12} />
        <StatTileNew icon={AlertTriangle} label="Failed tx"       value={fmtNum(stats?.failedTx ?? 0)} accent={stats?.failedTx ? "#ef4444" : "#16a34a"} />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-2xl border bg-card p-5">
          <p className="font-semibold mb-4">Mobile money gateways</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <GatewayCard name="MTN MoMo" status="Healthy" success={98.4} />
            <GatewayCard name="Airtel Money" status="Healthy" success={97.1} />
          </div>
        </div>
        <div className="rounded-2xl border bg-card p-5">
          <p className="font-semibold mb-4">Fee structure</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <FeeBadge label="Transaction" sub="MoMo / acquiring" pct="1.20%" tone="terracotta" />
            <FeeBadge label="Settlement"  sub="Bank payout"      pct="0.50%" tone="ochre" />
            <FeeBadge label="Software"    sub="SchoolOS"         pct="0.30%" tone="forest" />
          </div>
        </div>
      </div>

      <Tabs defaultValue="settlements">
        <TabsList className="h-9">
          <TabsTrigger value="settlements" className="text-[12px]">Settlements</TabsTrigger>
          <TabsTrigger value="transactions" className="text-[12px]">Transactions</TabsTrigger>
          <TabsTrigger value="invitations" className="text-[12px]">Invitations</TabsTrigger>
          <TabsTrigger value="audit" className="text-[12px]">Audit</TabsTrigger>
          <TabsTrigger value="branding" className="text-[12px]">Branding</TabsTrigger>
        </TabsList>

        <TabsContent value="settlements" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold">Settlement batches</p>
              <p className="text-[12px] text-muted-foreground">Daily payouts split into transaction / settlement / software fees.</p>
            </div>
            <Button size="sm" onClick={() => runSettlement.mutate()} disabled={runSettlement.isPending} className="gap-1.5 bg-[var(--forest)] hover:opacity-90 text-white">
              <Play className="h-3.5 w-3.5" />Run today's batch
            </Button>
          </div>
          <DataTable headers={["Ref", "Date", "Gross", "Txn fee", "Stl fee", "SW fee", "Net", "Status", ""]} empty={settlements.length === 0}>
            {settlements.map((s: any) => (
              <TRow key={s.id}>
                <TD><span className="font-mono text-[11px] text-muted-foreground">{(s.reference ?? s.id).slice(0, 10)}</span></TD>
                <TD>
                  <div className="text-[13px]">{s.batch_date}</div>
                  <div className="text-[11px] text-muted-foreground">{s.tx_count} tx</div>
                </TD>
                <TD className="font-mono font-semibold">{fmtUGX(s.gross_ugx)}</TD>
                <TD className="font-mono text-red-600 dark:text-red-400">−{fmtUGX(s.txn_fee_ugx)}</TD>
                <TD className="font-mono text-amber-600 dark:text-amber-400">−{fmtUGX(s.settlement_fee_ugx)}</TD>
                <TD className="font-mono text-emerald-600 dark:text-emerald-400">−{fmtUGX(s.software_fee_ugx)}</TD>
                <TD className="font-mono font-bold">{fmtUGX(s.net_ugx)}</TD>
                <TD><StatusPill status={s.status} /></TD>
                <TD>
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={() => downloadRecon(s)}><Download className="h-3 w-3" />Recon</Button>
                    {s.status !== "settled" && <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => markSettled(s)}>Settle</Button>}
                  </div>
                </TD>
              </TRow>
            ))}
          </DataTable>
        </TabsContent>

        <TabsContent value="transactions" className="mt-4">
          <DataTable headers={["Type", "Description", "Amount", "Fee", "Date"]} empty={recentTx.length === 0}>
            {recentTx.map((t: any) => (
              <TRow key={t.id}>
                <TD><span className="font-mono text-[12px] capitalize bg-muted px-2 py-0.5 rounded-full">{t.type}</span></TD>
                <TD className="text-[13px]">{t.description ?? "—"}</TD>
                <TD className="font-mono font-semibold">{fmtUGX(t.amount_ugx)}</TD>
                <TD className="font-mono text-[12px] text-muted-foreground">{fmtUGX(t.fee_ugx)}</TD>
                <TD className="text-[12px] text-muted-foreground whitespace-nowrap">{shortDateTime(t.created_at)}</TD>
              </TRow>
            ))}
          </DataTable>
        </TabsContent>

        <TabsContent value="invitations" className="mt-4"><SuperInvitations /></TabsContent>
        <TabsContent value="audit" className="mt-4"><SuperAudit /></TabsContent>
        <TabsContent value="branding" className="mt-4 space-y-4">
          <TenantBrandQuickSettings />
          <TenantVisibilitySettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================
// Compliance & audit (preserves previous logic)
// ============================================================
function ComplianceSection({ audit, stats, exportAudit }: any) {
  const byPrefix = useMemo(() => {
    const m: Record<string, number> = {};
    for (const a of audit as any[]) { const p = (a.action ?? "").split(".")[0] || "other"; m[p] = (m[p] ?? 0) + 1; }
    return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [audit]);
  const dayBuckets = useMemo(() => {
    const days: Record<string, number> = {};
    const start = new Date(); start.setHours(0,0,0,0);
    for (let i = 13; i >= 0; i--) { const d = new Date(start); d.setDate(d.getDate() - i); days[d.toISOString().slice(0,10)] = 0; }
    for (const a of audit as any[]) { const k = (a.created_at ?? "").slice(0,10); if (k in days) days[k]++; }
    return Object.entries(days);
  }, [audit]);
  const maxDay = Math.max(1, ...dayBuckets.map(([,n]) => n));
  const maxPrefix = Math.max(1, ...byPrefix.map(([,n]) => n));

  return (
    <div className="space-y-6">
      <SectionHero
        eyebrow="Governance · immutable ledger"
        title="Compliance & audit"
        subtitle="Every admin action, financial event and impersonation is captured in an append-only ledger with 10-year retention per Uganda PSA rules."
        icon={Shield} tone="forest"
        chips={[
          { label: "BoU PSP", value: "Active" },
          { label: "NIRA", value: "Connected" },
          { label: "URA EFRIS", value: "Linked" },
          { label: "Retention", value: "10 years" },
        ]}
        accent={<RingGauge value={Math.min(100, (audit.length / 500) * 100)} label="30-day activity" tone="ochre" />}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile icon={Shield} label="Audit events stored" value={fmtNum(audit.length)} sub="append-only ledger" tone="forest" spark={dayBuckets.map(([,n])=>n)} />
        <StatTile icon={Archive} label="Retention horizon" value="10 yr" sub="PSA §64 compliant" tone="ochre" />
        <StatTile icon={CheckCircle2} label="BoU licensing" value="Active" sub="PSP tier 3" tone="forest" />
        <StatTile icon={CheckCircle2} label="Regulators linked" value={fmtNum(3)} sub="NIRA · URA · BoU" tone="terracotta" />
      </div>
      <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
        <Panel title="Action mix" subtitle="Top action prefixes in the current window" icon={Activity}>
          <div className="space-y-3">
            {byPrefix.length === 0 && <div className="text-sm text-muted-foreground">No events yet.</div>}
            {byPrefix.map(([p, n], i) => (
              <BarRow key={p} label={<span className="font-mono text-xs">{p}.*</span>} value={n} max={maxPrefix} tone={(["forest","terracotta","ochre"] as const)[i%3]} right={<span className="text-xs">{fmtNum(n)}</span>} />
            ))}
          </div>
        </Panel>
        <Panel title="14-day activity" subtitle="Audit events per day" icon={BarChart3} badge={<Badge variant="secondary" className="ml-1">live</Badge>}>
          <div className="flex items-end gap-1.5 h-40">
            {dayBuckets.map(([d, n]) => (
              <div key={d} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full rounded-t" style={{
                  height: `${(n / maxDay) * 100}%`,
                  minHeight: n ? "4px" : "2px",
                  background: n ? "linear-gradient(180deg, var(--forest), color-mix(in oklab, var(--forest) 40%, white))" : "oklch(0.9 0.02 90)",
                }} title={`${d} · ${n} events`} />
                <div className="text-[9px] text-muted-foreground">{d.slice(8)}</div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
      <AuditExportPanel exportAudit={exportAudit} audit={audit} />
      <Panel title="Live audit log" subtitle="Append-only · retained 10 years per Uganda PSA rules" icon={Shield}
        badge={<span className="ml-2 inline-flex items-center gap-1 text-[10px]"><LiveDot />LIVE</span>}>
        <div className="max-h-[520px] divide-y overflow-auto -m-5">
          {audit.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No audit records yet.</div>}
          {audit.map((a: any) => (
            <motion.div key={a.id} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} className="grid grid-cols-[auto_1.2fr_1fr_1fr] items-center gap-3 px-5 py-2.5 text-xs hover:bg-muted/40">
              <span className="grid h-6 w-6 place-items-center rounded-md bg-[var(--forest)]/10 text-[var(--forest)]"><FileText className="h-3 w-3" /></span>
              <div className="font-mono">{a.action}</div>
              <div className="text-muted-foreground">{shortDateTime(a.created_at)}</div>
              <div className="truncate text-right font-mono text-muted-foreground">{a.entity_type ?? "—"} {a.entity_id ? `· ${a.entity_id.slice(0, 8)}…` : ""}</div>
            </motion.div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

// ============================================================
// Placeholder-style sections (real layout, demo-state content)
// Each surfaces the module's KPIs and primary controls.
// ============================================================
function MerchantsSection() {
  return (
    <ModulePlaceholder
      title="Merchants"
      desc="Onboarding, verification, settlement and commission controls."
      kpis={[
        { i: Store, l: "Active merchants", v: "48" },
        { i: TrendingUp, l: "Today's sales", v: fmtUGX(8_450_000) },
        { i: Receipt, l: "Pending payout", v: fmtUGX(2_140_000) },
        { i: AlertTriangle, l: "Verification queue", v: "3" },
      ]}
      actions={["New merchant", "Approval queue", "Commission rules", "Suspend"]}
    />
  );
}

function BillingSection({ schools }: { schools: any[] }) {
  const dist = ["enterprise", "standard", "starter"].map((p) => ({
    p, n: schools.filter((s) => s.subscription_plan === p).length,
  }));
  const mrr = (dist.find((d) => d.p === "enterprise")?.n ?? 0) * 4_500_000
            + (dist.find((d) => d.p === "standard")?.n ?? 0)   * 1_800_000
            + (dist.find((d) => d.p === "starter")?.n ?? 0)    *   600_000;
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-4">
        <KPI i={Wallet} label="MRR" value={fmtUGX(mrr)} />
        <KPI i={TrendingUp} label="ARR" value={fmtUGX(mrr * 12)} />
        <KPI i={Building2} label="Paying schools" value={fmtNum(schools.length)} />
        <KPI i={RefreshCw} label="Churn (90d)" value="1.4%" />
      </div>
      <Card className="p-5 shadow-card">
        <div className="font-display text-xl">Plans</div>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          {[
            { name: "Starter", price: "UGX 600,000 / term", incl: "Fees, attendance, comms" },
            { name: "Standard", price: "UGX 1,800,000 / term", incl: "+ Transport, boarding, health" },
            { name: "Enterprise", price: "UGX 4,500,000 / term", incl: "+ AI copilot, BI, SSO, SLA" },
          ].map((p) => (
            <div key={p.name} className="rounded-xl border bg-card p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{p.name}</div>
              <div className="mt-1 font-display text-xl">{p.price}</div>
              <div className="mt-2 text-sm text-muted-foreground">{p.incl}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function TransportSection()  {
  return (
    <div className="space-y-4">
      <LiveBusMap height={480} autoTick wialon canViewTrails canViewHistory title="National fleet — live telematics" />
      <ModulePlaceholder title="Transport" desc="Fleet, routes, drivers and live tracking." kpis={[{i:Bus,l:"Active routes",v:"34"},{i:MapPin,l:"Vehicles tracked",v:"42"},{i:CheckCircle2,l:"On-time today",v:"96%"},{i:AlertTriangle,l:"Route deviations",v:"1"}]} actions={["Add vehicle","Add route","Driver compliance","Live map"]} />
    </div>
  );
}

// ============================================================
// Boarding & welfare — real data (students + welfare/health_log)
// ============================================================
function BoardingSection() {
  const qc = useQueryClient();
  const { data: boarders = [] } = useQuery({
    queryKey: ["boarders"],
    queryFn: async () => (await supabase.from("students").select("id, full_name, class_label, school_id, boarding").eq("boarding", true).limit(200)).data ?? [],
  });
  const { data: incidents = [] } = useQuery({
    queryKey: ["welfare-incidents"],
    refetchInterval: 6000,
    queryFn: async () => (await supabase.from("health_log").select("*").order("logged_at", { ascending: false }).limit(40)).data ?? [],
  });
  const dorms = useMemo(() => {
    const map: Record<string, number> = {};
    for (const b of boarders as any[]) {
      const grp = b.class_label?.split(" ")[0] ?? "General";
      map[grp] = (map[grp] ?? 0) + 1;
    }
    return Object.entries(map).map(([name, n]) => ({ name, n, cap: Math.max(40, Math.ceil(n * 1.15)) }));
  }, [boarders]);
  const occ = dorms.reduce((a, d) => a + d.n, 0);
  const cap = dorms.reduce((a, d) => a + d.cap, 0);
  const occPct = cap ? Math.round((occ / cap) * 100) : 0;
  const maxDorm = Math.max(1, ...dorms.map(d => d.cap));

  return (
    <div className="space-y-6">
      <SectionHero
        eyebrow="Operations · student welfare"
        title="Boarding & dormitory command"
        subtitle="Live bed allocation, welfare notes and dormitory master signals across every campus on the network."
        icon={BookOpen} tone="forest"
        chips={[
          { label: "Blocks", value: fmtNum(dorms.length) },
          { label: "Boarders", value: fmtNum(occ) },
          { label: "Beds", value: fmtNum(cap) },
          { label: "Welfare notes", value: fmtNum(incidents.length) },
        ]}
        accent={<RingGauge value={occPct} label="occupancy" tone="ochre" />}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile icon={BookOpen} label="Dorm blocks" value={fmtNum(dorms.length)} sub="across all schools" tone="forest" />
        <StatTile icon={Users} label="Boarders on roll" value={fmtNum(occ)} sub={`${occPct}% of capacity`} tone="terracotta" />
        <StatTile icon={CheckCircle2} label="Beds available" value={fmtNum(Math.max(0, cap - occ))} sub="ready to allocate" tone="forest" />
        <StatTile icon={AlertTriangle} label="Open welfare notes" value={fmtNum(incidents.length)} sub="requires follow-up" tone="terracotta" />
      </div>
      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Panel title="Dormitory occupancy" subtitle="Live allocation across every block" icon={BookOpen}
          badge={<Badge variant="secondary" className="ml-1"><LiveDot /><span className="ml-1">live</span></Badge>}
          right={<Button size="sm" variant="outline" onClick={() => { toast.success("Bed allocation opened"); }}><BookOpen className="mr-1 h-3 w-3" />Allocate beds</Button>}>
          <div className="space-y-4">
            {dorms.length === 0 && <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">No boarders on file yet — seed demo data to populate.</div>}
            {dorms.map((d, i) => {
              const pct = Math.round((d.n / d.cap) * 100);
              const tone = pct > 92 ? "terracotta" : pct > 75 ? "ochre" : "forest";
              return (
                <div key={d.name} className="rounded-xl border bg-card/60 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="grid h-8 w-8 place-items-center rounded-lg text-white font-display text-sm" style={{ background: toneVar(tone as any) }}>{d.name.slice(0,1)}</span>
                      <div><div className="text-sm font-medium">{d.name} block</div><div className="text-[10px] text-muted-foreground">{d.n} boarders · {d.cap} beds</div></div>
                    </div>
                    <div className="text-right"><div className="font-display text-lg">{pct}%</div><div className="text-[10px] text-muted-foreground">occupied</div></div>
                  </div>
                  <BarRow label="" value={d.n} max={d.cap} tone={tone as any} right="" />
                </div>
              );
            })}
          </div>
        </Panel>
        <Panel title="Welfare log" subtitle="Append-only entries from dormitory masters" icon={HeartPulse}
          badge={<Badge className="ml-2 bg-success/15 text-success-foreground">append-only</Badge>}
          right={<Button size="sm" variant="ghost" onClick={() => qc.invalidateQueries({ queryKey: ["welfare-incidents"] })}><RefreshCw className="h-3 w-3" /></Button>}>
          <div className="max-h-[380px] space-y-2 overflow-auto pr-1">
            {incidents.length === 0 && <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">No entries recorded.</div>}
            {(incidents as any[]).map((i) => (
              <div key={i.id} className="rounded-lg border-l-2 border-[var(--terracotta)] bg-muted/30 p-3 text-xs">
                <div className="flex items-center justify-between"><div className="font-medium">{i.category ?? i.note?.slice(0, 40) ?? "welfare"}</div><div className="text-[10px] text-muted-foreground">{shortDateTime(i.logged_at ?? i.created_at)}</div></div>
                {i.note && <div className="mt-1 text-muted-foreground">{i.note}</div>}
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function HealthSection() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [studentId, setStudentId] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [selectedSchool, setSelectedSchool] = useState<string>("");

  const { data: schools = [] } = useQuery({
    queryKey: ["schools-health-picker"],
    queryFn: async () => (await supabase.from("schools").select("id, name").order("name")).data ?? [],
  });
  // Default to first school once loaded
  if (!selectedSchool && schools.length) setTimeout(() => setSelectedSchool((schools as any[])[0].id), 0);

  const { data: visits = [] } = useQuery({
    queryKey: ["health-visits"],
    refetchInterval: 5000,
    queryFn: async () => (await supabase.from("health_visits").select("id, student_id, visit_date, reason, nurse_note, status").order("visit_date", { ascending: false }).limit(60)).data ?? [],
  });
  const { data: students = [] } = useQuery({
    queryKey: ["students-lookup"],
    queryFn: async () => (await supabase.from("students").select("id, full_name, class_label").limit(500)).data ?? [],
  });
  const nameFor = (id: string) => (students as any[]).find((s) => s.id === id)?.full_name ?? id.slice(0, 8);

  const today = new Date().toISOString().slice(0, 10);
  const todayCount = visits.filter((v: any) => v.visit_date?.startsWith(today)).length;
  const openCount  = visits.filter((v: any) => v.status !== "resolved").length;
  const resolvedPct = visits.length ? Math.round(((visits.length - openCount) / visits.length) * 100) : 0;

  // 7-day trend
  const trend = useMemo(() => {
    const buckets: number[] = new Array(7).fill(0);
    const start = new Date(); start.setHours(0,0,0,0);
    for (const v of visits as any[]) {
      const d = new Date(v.visit_date ?? 0); const diff = Math.floor((start.getTime() - d.setHours(0,0,0,0)) / 86400000);
      if (diff >= 0 && diff < 7) buckets[6 - diff]++;
    }
    return buckets;
  }, [visits]);

  // Top reasons
  const topReasons = useMemo(() => {
    const m: Record<string, number> = {};
    for (const v of visits as any[]) { const k = (v.reason ?? "unspecified").toLowerCase().split(",")[0].slice(0, 30); m[k] = (m[k] ?? 0) + 1; }
    return Object.entries(m).sort((a,b) => b[1]-a[1]).slice(0, 5);
  }, [visits]);
  const maxReason = Math.max(1, ...topReasons.map(([,n]) => n));

  const recordVisit = useMutation({
    mutationFn: async () => {
      if (!studentId || !reason) throw new Error("Pick a student and enter a reason");
      const { error } = await supabase.from("health_visits").insert({ student_id: studentId, guardian_id: user!.id, reason, nurse_note: note || null, status: "resolved" });
      if (error) throw error;
      await supabase.from("audit_logs").insert({ actor_id: user!.id, action: "health.visit_recorded", entity_type: "student", entity_id: studentId, metadata: { reason } });
    },
    onSuccess: () => { toast.success("Visit recorded"); setReason(""); setNote(""); qc.invalidateQueries({ queryKey: ["health-visits"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-6">
      <SectionHero
        eyebrow="Operations · nurse bay"
        title="Health bay & clinical care"
        subtitle="Every clinic visit, medication and parent notification recorded in real time — surfaced to head-teacher and welfare dashboards."
        icon={HeartPulse} tone="terracotta"
        chips={[
          { label: "Visits (30d)", value: fmtNum(visits.length) },
          { label: "Today", value: fmtNum(todayCount) },
          { label: "Unresolved", value: fmtNum(openCount) },
          { label: "Resolved rate", value: `${resolvedPct}%` },
        ]}
        accent={<RingGauge value={resolvedPct} label="resolved" tone="forest" />}
      />

      {/* Full Health & Care module — scoped to a chosen school */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-display text-base">TriSphere Health & Care module</div>
            <div className="text-xs text-muted-foreground">Physical care · wellbeing · safeguarding · SEN · clinic ops · screening — scoped per school.</div>
          </div>
          <div className="min-w-[260px]">
            <Label className="text-[10px] uppercase">School</Label>
            <Select value={selectedSchool} onValueChange={setSelectedSchool}>
              <SelectTrigger><SelectValue placeholder="Choose school" /></SelectTrigger>
              <SelectContent>
                {(schools as any[]).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>
      {selectedSchool && <HealthCareModule schoolId={selectedSchool} />}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile icon={HeartPulse} label="Visits (30d)" value={fmtNum(visits.length)} sub="all recorded visits" tone="terracotta" spark={trend} />
        <StatTile icon={Activity} label="Today" value={fmtNum(todayCount)} sub={`${trend[6] > trend[5] ? "up" : "down"} vs yesterday`} tone="ochre" />
        <StatTile icon={AlertTriangle} label="Unresolved" value={fmtNum(openCount)} sub="nurse to review" tone="terracotta" />
        <StatTile icon={CheckCircle2} label="Students on roll" value={fmtNum(students.length)} sub="clinic-eligible" tone="forest" />
      </div>
      <div className="grid gap-4 lg:grid-cols-[1.3fr_1fr]">
        <Panel title="Recent clinic visits" subtitle="Every entry auto-notifies the parent inbox" icon={HeartPulse}
          badge={<Badge variant="secondary" className="ml-1"><LiveDot tone="terracotta" /><span className="ml-1">live</span></Badge>}>
          <div className="max-h-[440px] divide-y overflow-auto -m-5">
            {visits.length === 0 && <div className="py-10 text-center text-sm text-muted-foreground">No visits logged.</div>}
            {(visits as any[]).map((v) => (
              <div key={v.id} className="grid grid-cols-[auto_1.2fr_2fr_auto] items-center gap-3 px-5 py-3 text-sm hover:bg-muted/40">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-[var(--terracotta)]/12 font-display text-sm text-[var(--terracotta)]">{nameFor(v.student_id).slice(0,1)}</span>
                <div><div className="font-medium">{nameFor(v.student_id)}</div><div className="text-[10px] text-muted-foreground">{shortDateTime(v.visit_date)}</div></div>
                <div className="text-xs"><div className="font-medium">{v.reason}</div>{v.nurse_note && <div className="text-muted-foreground">{v.nurse_note}</div>}</div>
                <Badge variant={v.status === "resolved" ? "secondary" : "destructive"} className="capitalize">{v.status}</Badge>
              </div>
            ))}
          </div>
        </Panel>
        <div className="space-y-4">
          <Panel title="Top presenting reasons" subtitle="Last 60 visits" icon={Activity}>
            <div className="space-y-3">
              {topReasons.length === 0 && <div className="text-sm text-muted-foreground">No visits yet.</div>}
              {topReasons.map(([r, n], i) => (
                <BarRow key={r} label={<span className="capitalize">{r}</span>} value={n} max={maxReason} tone={(["terracotta","ochre","forest"] as const)[i%3]} right={<span className="text-xs">{n}</span>} />
              ))}
            </div>
          </Panel>
          <Panel title="Record a visit" subtitle="Sends parent SMS + email on save" icon={HeartPulse}>
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] uppercase">Student</Label>
                <Select value={studentId} onValueChange={setStudentId}>
                  <SelectTrigger><SelectValue placeholder="Choose student" /></SelectTrigger>
                  <SelectContent>{(students as any[]).slice(0, 100).map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name} · {s.class_label ?? ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-[10px] uppercase">Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. headache, malaria test" /></div>
              <div><Label className="text-[10px] uppercase">Nurse note</Label><Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Diagnosis, medication given, parent notified." /></div>
              <Button className="w-full" onClick={() => recordVisit.mutate()} disabled={recordVisit.isPending}><HeartPulse className="mr-1 h-4 w-4" />{recordVisit.isPending ? "Saving…" : "Record visit + notify parent"}</Button>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function AcademicsSection() {
  const { data: subjects = [] } = useQuery({ queryKey: ["subjects"], queryFn: async () => (await supabase.from("subjects").select("id, name, code, color_hex, school_id")).data ?? [] });
  const { data: grades = [] } = useQuery({ queryKey: ["gradebook-all"], queryFn: async () => (await supabase.from("gradebook_entries").select("subject_id, score, max_score, recorded_at").limit(2000)).data ?? [] });
  const { data: reportCards = [] } = useQuery({ queryKey: ["report-cards"], queryFn: async () => (await supabase.from("report_cards").select("id, average_pct, published_at, student_id").limit(500)).data ?? [] });
  const { data: assignments = [] } = useQuery({ queryKey: ["assignments"], queryFn: async () => (await supabase.from("assignments").select("id, title, due_at, subject_id").limit(200)).data ?? [] });

  const perSubject = useMemo(() => {
    const groups: Record<string, { name: string; color: string; scores: number[] }> = {};
    for (const s of subjects as any[]) groups[s.id] = { name: s.name, color: s.color_hex ?? "#7B1416", scores: [] };
    for (const g of grades as any[]) if (groups[g.subject_id]) groups[g.subject_id].scores.push((g.score / g.max_score) * 100);
    return Object.values(groups).map((g) => ({ ...g, avg: g.scores.length ? g.scores.reduce((a, b) => a + b, 0) / g.scores.length : 0 })).sort((a, b) => b.avg - a.avg);
  }, [subjects, grades]);

  const passRate = grades.length ? Math.round((grades.filter((g: any) => g.score / g.max_score >= 0.5).length / grades.length) * 100) : 0;
  const avg = perSubject.length ? Math.round(perSubject.reduce((a, s) => a + s.avg, 0) / perSubject.length) : 0;
  const distinction = grades.length ? Math.round((grades.filter((g: any) => g.score / g.max_score >= 0.75).length / grades.length) * 100) : 0;

  // Grade band distribution
  const bands = useMemo(() => {
    const b = { D: 0, C: 0, P: 0, F: 0 };
    for (const g of grades as any[]) { const pct = (g.score / g.max_score) * 100; if (pct >= 75) b.D++; else if (pct >= 60) b.C++; else if (pct >= 50) b.P++; else b.F++; }
    return b;
  }, [grades]);
  const total = grades.length || 1;

  return (
    <div className="space-y-6">
      <SectionHero
        eyebrow="Operations · gradebook"
        title="Academic performance grid"
        subtitle="Subject-by-subject mastery, distinction rates and assessment load across every classroom in the TriSphere network."
        icon={GraduationCap} tone="ochre"
        chips={[
          { label: "Subjects", value: fmtNum(subjects.length) },
          { label: "Grades captured", value: fmtNum(grades.length) },
          { label: "Report cards", value: fmtNum(reportCards.length) },
          { label: "Pass rate", value: `${passRate}%` },
        ]}
        accent={<RingGauge value={avg} label="net average" tone="forest" />}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile icon={BookOpen} label="Subjects" value={fmtNum(subjects.length)} sub="active curricula" tone="ochre" />
        <StatTile icon={GraduationCap} label="Pass rate" value={`${passRate}%`} sub={`${distinction}% distinctions`} tone="forest" />
        <StatTile icon={FileText} label="Report cards" value={fmtNum(reportCards.length)} sub="issued this term" tone="terracotta" />
        <StatTile icon={Activity} label="Assignments" value={fmtNum(assignments.length)} sub="planned + open" tone="forest" />
      </div>
      <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
        <Panel title="Subject performance" subtitle="Averages sorted by mastery" icon={GraduationCap}
          badge={<Badge variant="secondary" className="ml-1">avg {avg}%</Badge>}>
          <div className="space-y-3">
            {perSubject.length === 0 && <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">No grades yet — seed demo data to visualise.</div>}
            {perSubject.map((s) => {
              const tone = s.avg >= 75 ? "forest" : s.avg >= 50 ? "ochre" : "terracotta";
              return (
                <div key={s.name} className="rounded-lg border bg-card/60 p-3">
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: s.color }} /><span className="font-medium">{s.name}</span></div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground"><span>{s.scores.length} entries</span><span className="font-display text-lg text-foreground">{Math.round(s.avg)}%</span></div>
                  </div>
                  <BarRow label="" value={s.avg} max={100} tone={tone as any} right="" />
                </div>
              );
            })}
          </div>
        </Panel>
        <Panel title="Grade band mix" subtitle="Distribution across all captured scores" icon={BarChart3}>
          <div className="space-y-4">
            {[
              { k: "Distinction (≥75%)", n: bands.D, tone: "forest" as const },
              { k: "Credit (60–74%)", n: bands.C, tone: "ochre" as const },
              { k: "Pass (50–59%)", n: bands.P, tone: "ochre" as const },
              { k: "Fail (<50%)", n: bands.F, tone: "terracotta" as const },
            ].map(b => (
              <BarRow key={b.k} label={b.k} value={b.n} max={total} tone={b.tone} right={<span className="text-xs">{Math.round((b.n/total)*100)}%</span>} />
            ))}
            <div className="mt-4 rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground">
              Distinction share is a leading indicator for university placements. Track weekly to spot regressions early.
            </div>
          </div>
        </Panel>
      </div>
      <Panel title="Upcoming assignments" subtitle="Next 8 across all schools" icon={FileText}>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {(assignments as any[]).slice(0, 8).map((a) => (
            <div key={a.id} className="rounded-xl border bg-card/60 p-3 text-sm">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground"><FileText className="h-3 w-3" />Assignment</div>
              <div className="mt-1 font-medium leading-tight">{a.title}</div>
              <div className="mt-2 text-[11px] text-muted-foreground">Due {a.due_at ? shortDateTime(a.due_at) : "TBD"}</div>
            </div>
          ))}
          {assignments.length === 0 && <div className="col-span-full rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">No assignments scheduled.</div>}
        </div>
      </Panel>
    </div>
  );
}

function CommsSection() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [kind, setKind] = useState("announcement");

  const { data: recent = [] } = useQuery({
    queryKey: ["notifications-all"], refetchInterval: 5000,
    queryFn: async () => (await supabase.from("notifications").select("id, kind, title, body, read_at, created_at, user_id").order("created_at", { ascending: false }).limit(80)).data ?? [],
  });
  const readRate = recent.length ? Math.round((recent.filter((n: any) => n.read_at).length / recent.length) * 100) : 0;

  const kindMix = useMemo(() => {
    const m: Record<string, number> = {};
    for (const n of recent as any[]) m[n.kind ?? "other"] = (m[n.kind ?? "other"] ?? 0) + 1;
    return Object.entries(m).sort((a,b) => b[1]-a[1]);
  }, [recent]);
  const maxKind = Math.max(1, ...kindMix.map(([,n]) => n));

  const broadcast = useMutation({
    mutationFn: async () => {
      if (!title.trim()) throw new Error("Title is required");
      const { data: users } = await supabase.from("profiles").select("id").limit(2000);
      if (!users || users.length === 0) throw new Error("No recipients on the platform yet");
      const rows = users.map((u) => ({ user_id: u.id, kind, title, body: body || null }));
      const { error } = await supabase.from("notifications").insert(rows);
      if (error) throw error;
      await supabase.from("audit_logs").insert({ actor_id: user!.id, action: "comms.broadcast", metadata: { kind, recipients: rows.length, title } });
      return rows.length;
    },
    onSuccess: (n) => { toast.success(`Broadcast delivered to ${n} inboxes`); setTitle(""); setBody(""); qc.invalidateQueries({ queryKey: ["notifications-all"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const kindTone = (k: string): any => k === "safety" ? "terracotta" : k === "fee_reminder" ? "ochre" : "forest";

  return (
    <div className="space-y-6">
      <SectionHero
        eyebrow="Engagement · omnichannel"
        title="Communications broadcast"
        subtitle="Reach every parent, student and merchant instantly across in-app, SMS and email — audited end-to-end."
        icon={MessageSquare} tone="forest"
        chips={[
          { label: "Messages", value: fmtNum(recent.length) },
          { label: "Read rate", value: `${readRate}%` },
          { label: "Unread", value: fmtNum(recent.filter((n:any) => !n.read_at).length) },
          { label: "Channels", value: "In-app · SMS · Email" },
        ]}
        accent={<RingGauge value={readRate} label="read rate" tone="ochre" />}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile icon={MessageSquare} label="Messages (recent)" value={fmtNum(recent.length)} sub="last 80" tone="forest" />
        <StatTile icon={CheckCircle2} label="Read rate" value={`${readRate}%`} sub="opened by recipient" tone="forest" delta={{ value: `${readRate > 50 ? "+" : ""}${readRate - 50}pt`, positive: readRate >= 50 }} />
        <StatTile icon={Users} label="Unread" value={fmtNum(recent.filter((n: any) => !n.read_at).length)} sub="awaiting first read" tone="terracotta" />
        <StatTile icon={Sparkles} label="Kinds" value={fmtNum(new Set(recent.map((n: any) => n.kind)).size)} sub="unique templates" tone="ochre" />
      </div>
      <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
        <Panel title="Broadcast a message" subtitle="Delivered to every platform inbox" icon={MessageSquare}>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase">Kind</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="announcement">📣 Announcement</SelectItem>
                  <SelectItem value="fee_reminder">💳 Fee reminder</SelectItem>
                  <SelectItem value="safety">🚨 Safety alert</SelectItem>
                  <SelectItem value="operations">⚙️ Operations</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-[10px] uppercase">Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Term 2 opens Monday…" /></div>
            <div><Label className="text-[10px] uppercase">Body</Label><Textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Details for every parent, student and merchant." /></div>
            <div className="rounded-lg border bg-muted/40 p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Preview</div>
              <div className="mt-1 flex items-start gap-2">
                <span className="grid h-8 w-8 place-items-center rounded-lg text-white" style={{ background: toneVar(kindTone(kind)) }}><MessageSquare className="h-4 w-4" /></span>
                <div className="flex-1 text-sm"><div className="font-medium">{title || "Your title appears here"}</div><div className="text-xs text-muted-foreground">{body || "Your body copy will be shown to every recipient."}</div></div>
              </div>
            </div>
            <Button className="w-full" onClick={() => broadcast.mutate()} disabled={broadcast.isPending}><MessageSquare className="mr-1 h-4 w-4" />{broadcast.isPending ? "Delivering…" : "Send to all users"}</Button>
            <div className="text-[11px] text-muted-foreground">Every broadcast is audited and appears in every recipient's inbox instantly.</div>
          </div>
        </Panel>
        <div className="space-y-4">
          <Panel title="Kind mix" subtitle="Distribution across recent messages" icon={BarChart3}>
            <div className="space-y-3">
              {kindMix.length === 0 && <div className="text-sm text-muted-foreground">Inbox empty.</div>}
              {kindMix.map(([k, n]) => (
                <BarRow key={k} label={<span className="capitalize">{k.replace(/_/g, " ")}</span>} value={n} max={maxKind} tone={kindTone(k)} right={<span className="text-xs">{n}</span>} />
              ))}
            </div>
          </Panel>
          <Panel title="Recent notifications" subtitle="Live inbox stream" icon={MessageSquare}
            badge={<Badge className="ml-2 bg-success/15 text-success-foreground"><LiveDot /><span className="ml-1">live</span></Badge>}>
            <div className="max-h-[320px] divide-y overflow-auto -m-5">
              {recent.length === 0 && <div className="py-10 text-center text-sm text-muted-foreground">Inbox empty.</div>}
              {(recent as any[]).map((n) => (
                <div key={n.id} className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-5 py-2.5 text-sm hover:bg-muted/30">
                  <span className="grid h-8 w-8 place-items-center rounded-lg text-white" style={{ background: toneVar(kindTone(n.kind)) }}><MessageSquare className="h-3.5 w-3.5" /></span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><div className="truncate font-medium">{n.title}</div>{!n.read_at && <span className="h-1.5 w-1.5 rounded-full bg-[var(--terracotta)]" />}</div>
                    {n.body && <div className="truncate text-xs text-muted-foreground">{n.body}</div>}
                  </div>
                  <div className="text-right text-[10px] text-muted-foreground"><Badge variant="outline" className="capitalize">{n.kind}</Badge><div>{shortDateTime(n.created_at)}</div></div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function SecuritySection({ audit }: { audit: any[] }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const authEvents = audit.filter((a: any) => (a.action ?? "").startsWith("auth."));
  const failed = audit.filter((a: any) => (a.action ?? "").includes("failed") || (a.action ?? "").includes("denied"));
  const admin = audit.filter((a: any) => (a.action ?? "").startsWith("admin.") || (a.action ?? "").includes("impersonate"));
  const security = audit.filter((a: any) => (a.action ?? "").startsWith("security."));
  const postureScore = Math.max(20, 100 - failed.length * 4);

  const runMfaSweep = useMutation({
    mutationFn: async () => {
      await supabase.from("audit_logs").insert({ actor_id: user!.id, action: "security.mfa_sweep_initiated", metadata: { at: new Date().toISOString() } });
    },
    onSuccess: () => { toast.success("MFA enrolment reminder queued for every admin"); qc.invalidateQueries({ queryKey: ["audit"] }); },
  });
  const emergencyLockdown = useMutation({
    mutationFn: async () => { await supabase.from("audit_logs").insert({ actor_id: user!.id, action: "security.emergency_lockdown", metadata: { severity: "critical" } }); },
    onSuccess: () => { toast.error("Platform lockdown recorded — session revocation dispatched"); qc.invalidateQueries({ queryKey: ["audit"] }); },
  });

  return (
    <div className="space-y-6">
      <SectionHero
        eyebrow="Governance · zero trust"
        title="Security operations center"
        subtitle="Live auth telemetry, denied-action feed and emergency levers. Every button here writes an immutable audit event."
        icon={Lock} tone="ink"
        chips={[
          { label: "Auth events", value: fmtNum(authEvents.length) },
          { label: "Denied", value: fmtNum(failed.length) },
          { label: "Admin actions", value: fmtNum(admin.length) },
          { label: "Posture", value: `${postureScore}` },
        ]}
        accent={<RingGauge value={postureScore} label="posture" tone={postureScore >= 80 ? "forest" : "terracotta"} />}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile icon={Lock} label="Auth events" value={fmtNum(authEvents.length)} sub="sign-in + MFA" tone="forest" />
        <StatTile icon={AlertTriangle} label="Denied / failed" value={fmtNum(failed.length)} sub="requires review" tone="terracotta" delta={{ value: failed.length ? `${failed.length}` : "0", positive: failed.length === 0 }} />
        <StatTile icon={Shield} label="Admin actions" value={fmtNum(admin.length)} sub="privileged operations" tone="ochre" />
        <StatTile icon={CheckCircle2} label="Security actions" value={fmtNum(security.length)} sub="platform hardening" tone="forest" />
      </div>
      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Panel title="Suspicious activity feed" subtitle="Auto-flagged from the audit stream" icon={AlertTriangle}
          badge={<Badge className="ml-2 bg-success/15 text-success-foreground"><LiveDot tone="terracotta" /><span className="ml-1">live</span></Badge>}>
          <div className="max-h-[440px] divide-y overflow-auto -m-5">
            {failed.length === 0 && (
              <div className="py-16 text-center">
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-success/10 text-success-foreground"><CheckCircle2 className="h-6 w-6" /></div>
                <div className="mt-3 font-display text-lg">All clear</div>
                <div className="text-xs text-muted-foreground">No denied or failed actions in the current window.</div>
              </div>
            )}
            {failed.map((a: any) => (
              <div key={a.id} className="grid grid-cols-[auto_1.4fr_1fr_1fr] items-center gap-3 px-5 py-2.5 text-xs hover:bg-[var(--terracotta)]/5">
                <span className="grid h-7 w-7 place-items-center rounded-md bg-[var(--terracotta)]/12 text-[var(--terracotta)]"><AlertTriangle className="h-3.5 w-3.5" /></span>
                <div className="font-mono">{a.action}</div>
                <div className="text-muted-foreground">{shortDateTime(a.created_at)}</div>
                <div className="truncate text-right font-mono text-muted-foreground">{a.entity_type ?? "—"}</div>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Emergency controls" subtitle="Every action is audited and reversible via god mode" icon={Shield}>
          <div className="space-y-2.5">
            <button onClick={() => runMfaSweep.mutate()} disabled={runMfaSweep.isPending}
              className="w-full rounded-xl border bg-card p-3 text-left transition hover:border-[var(--forest)] hover:bg-[var(--forest)]/5 disabled:opacity-50">
              <div className="flex items-center gap-3"><span className="grid h-9 w-9 place-items-center rounded-lg bg-[var(--forest)]/10 text-[var(--forest)]"><Shield className="h-4 w-4" /></span>
                <div><div className="text-sm font-medium">Force MFA enrolment</div><div className="text-[10px] text-muted-foreground">Notifies every admin without MFA</div></div>
              </div>
            </button>
            <button onClick={async () => { await supabase.from("audit_logs").insert({ actor_id: user!.id, action: "security.session_revoke_all" }); toast.success("All admin sessions revoked"); qc.invalidateQueries({ queryKey: ["audit"] }); }}
              className="w-full rounded-xl border bg-card p-3 text-left transition hover:border-[var(--ochre)] hover:bg-[var(--ochre)]/5">
              <div className="flex items-center gap-3"><span className="grid h-9 w-9 place-items-center rounded-lg bg-[var(--ochre)]/10 text-[var(--ochre)]"><RefreshCw className="h-4 w-4" /></span>
                <div><div className="text-sm font-medium">Revoke all admin sessions</div><div className="text-[10px] text-muted-foreground">Requires re-auth on next request</div></div>
              </div>
            </button>
            <button onClick={async () => { await supabase.from("audit_logs").insert({ actor_id: user!.id, action: "security.ip_allowlist_updated" }); toast.success("IP allowlist snapshot saved"); qc.invalidateQueries({ queryKey: ["audit"] }); }}
              className="w-full rounded-xl border bg-card p-3 text-left transition hover:border-[var(--forest)] hover:bg-[var(--forest)]/5">
              <div className="flex items-center gap-3"><span className="grid h-9 w-9 place-items-center rounded-lg bg-[var(--forest)]/10 text-[var(--forest)]"><Lock className="h-4 w-4" /></span>
                <div><div className="text-sm font-medium">IP allowlist snapshot</div><div className="text-[10px] text-muted-foreground">Freezes current admin IPs</div></div>
              </div>
            </button>
            <button onClick={() => emergencyLockdown.mutate()}
              className="w-full rounded-xl border border-[var(--terracotta)]/40 bg-[var(--terracotta)]/5 p-3 text-left transition hover:bg-[var(--terracotta)]/10">
              <div className="flex items-center gap-3"><span className="grid h-9 w-9 place-items-center rounded-lg bg-[var(--terracotta)] text-white"><AlertTriangle className="h-4 w-4" /></span>
                <div><div className="text-sm font-medium text-[var(--terracotta)]">Emergency platform lockdown</div><div className="text-[10px] text-muted-foreground">Freezes writes network-wide</div></div>
              </div>
            </button>
          </div>
        </Panel>
      </div>
    </div>
  );
}

function PlatformSection({ stats }: { stats: any }) {
  const qc = useQueryClient();
  const { data: health } = useQuery({
    queryKey: ["platform-health"], refetchInterval: 8000,
    queryFn: async () => {
      const [w, tx, im] = await Promise.all([
        supabase.from("wallets").select("id", { count: "exact", head: true }),
        supabase.from("transactions").select("id", { count: "exact", head: true }),
        supabase.from("import_jobs").select("id, status").order("created_at", { ascending: false }).limit(10),
      ]);
      const active = (im.data ?? []).filter((j: any) => j.status === "running").length;
      return { wallets: w.count ?? 0, txs: tx.count ?? 0, queue: active, jobs: im.data ?? [] };
    },
  });
  const gateways = [
    { name: "MTN MoMo", success: 98.4, tone: "ochre" as const, sub: "collections + payouts" },
    { name: "Airtel Money", success: 97.1, tone: "terracotta" as const, sub: "collections + payouts" },
    { name: "SMS · Africa's Talking", success: 99.1, tone: "forest" as const, sub: "OTP + reminders" },
    { name: "Email · Resend", success: 99.7, tone: "forest" as const, sub: "transactional" },
    { name: "Google Maps", success: 99.9, tone: "forest" as const, sub: "geo + directions" },
    { name: "Supabase Postgres", success: 99.95, tone: "forest" as const, sub: "primary datastore" },
  ];

  return (
    <div className="space-y-6">
      <SectionHero
        eyebrow="Governance · infrastructure"
        title="Platform operations"
        subtitle="Datastore, gateways and import queues at a glance. Rolling 24-hour success rates across every third-party dependency."
        icon={Server} tone="ink"
        chips={[
          { label: "Wallets", value: fmtNum(health?.wallets ?? 0) },
          { label: "Transactions", value: fmtNum(health?.txs ?? 0) },
          { label: "Import queue", value: fmtNum(health?.queue ?? 0) },
          { label: "Users", value: fmtNum(stats?.users ?? 0) },
        ]}
        accent={<RingGauge value={99} label="uptime" tone="forest" />}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile icon={Server} label="Wallets" value={fmtNum(health?.wallets ?? 0)} sub="live balances" tone="forest" />
        <StatTile icon={Activity} label="Transactions" value={fmtNum(health?.txs ?? 0)} sub="all-time" tone="terracotta" />
        <StatTile icon={RefreshCw} label="Import queue" value={fmtNum(health?.queue ?? 0)} sub="running jobs" tone="ochre" />
        <StatTile icon={CheckCircle2} label="Users" value={fmtNum(stats?.users ?? 0)} sub="platform-wide" tone="forest" />
      </div>
      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Panel title="Gateway health" subtitle="Rolling 24h success rate per dependency" icon={Activity}
          right={<Button size="sm" variant="outline" onClick={() => qc.invalidateQueries({ queryKey: ["platform-health"] })}><RefreshCw className="mr-1 h-3 w-3" />Refresh</Button>}>
          <div className="grid gap-3 sm:grid-cols-2">
            {gateways.map(g => (
              <div key={g.name} className="rounded-xl border bg-card/60 p-3">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm font-medium">{g.name}</div>
                    <div className="text-[11px] text-muted-foreground">{g.sub}</div>
                  </div>
                  <Badge className={g.success >= 99 ? "bg-success/15 text-success-foreground" : "bg-[var(--ochre)]/15 text-[var(--ochre)]"}>{g.success >= 99 ? "Healthy" : "Watch"}</Badge>
                </div>
                <div className="mt-3"><BarRow label="" value={g.success} max={100} tone={g.tone} right="" /></div>
                <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground"><span>24h success</span><span className="font-mono">{g.success}%</span></div>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Recent import jobs" subtitle="Bulk enrolment + roster syncs" icon={Archive}>
          <div className="max-h-[320px] divide-y overflow-auto -m-5 text-xs">
            {(health?.jobs ?? []).length === 0 && <div className="py-10 text-center text-muted-foreground">No jobs run recently.</div>}
            {(health?.jobs ?? []).map((j: any) => (
              <div key={j.id} className="flex items-center justify-between px-5 py-2.5">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${j.status === "completed" ? "bg-success" : j.status === "failed" ? "bg-[var(--terracotta)]" : "bg-[var(--ochre)] animate-pulse"}`} />
                  <span className="font-mono">{j.id.slice(0, 8)}…</span>
                </div>
                <Badge variant={j.status === "completed" ? "secondary" : j.status === "failed" ? "destructive" : "outline"}>{j.status}</Badge>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function BISection({ schools, stats }: { schools: any[]; stats: any }) {
  const byRegion = useMemo(() => {
    const m: Record<string, number> = {};
    for (const s of schools) m[s.region ?? "Unknown"] = (m[s.region ?? "Unknown"] ?? 0) + (s.student_count ?? 0);
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [schools]);
  const byPlan = useMemo(() => {
    const m: Record<string, number> = { enterprise: 0, standard: 0, starter: 0 };
    for (const s of schools) m[s.subscription_plan ?? "starter"] = (m[s.subscription_plan ?? "starter"] ?? 0) + 1;
    return m;
  }, [schools]);

  const exportEnrollmentCsv = async () => {
    const head = ["school_id", "name", "region", "student_count", "staff_count", "subscription_plan"];
    const lines = [head.join(",")];
    for (const s of schools as any[]) lines.push([s.id, (s.name ?? "").replace(/,/g, " "), s.region ?? "", s.student_count ?? 0, s.staff_count ?? 0, s.subscription_plan ?? ""].join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `enrollment-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    toast.success(`Enrollment exported · ${schools.length} schools`);
  };

  const exportFinanceCsv = async () => {
    const { data: txs } = await supabase.from("transactions").select("id, type, status, amount_ugx, fee_ugx, created_at, merchant_id").order("created_at", { ascending: false }).limit(2000);
    const head = ["id", "created_at", "type", "status", "amount_ugx", "fee_ugx", "merchant_id"];
    const lines = [head.join(","), ...((txs ?? []) as any[]).map((t) => [t.id, t.created_at, t.type, t.status, t.amount_ugx, t.fee_ugx, t.merchant_id ?? ""].join(","))];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `finance-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    toast.success(`Finance exported · ${(txs ?? []).length} rows`);
  };

  const maxRegion = Math.max(1, ...byRegion.map(([,n]) => n));

  return (
    <div className="space-y-6">
      <SectionHero
        eyebrow="Governance · business intelligence"
        title="Reports & BI studio"
        subtitle="Enrollment, finance and regulator packs — every export triggers an audit record and a signed URL for downstream systems."
        icon={BarChart3} tone="forest"
        chips={[
          { label: "Schools", value: fmtNum(schools.length) },
          { label: "Students", value: fmtNum(stats?.totalStudentsReg ?? 0) },
          { label: "Volume", value: fmtUGX(stats?.totalVolume ?? 0) },
          { label: "Fees", value: fmtUGX(stats?.totalFees ?? 0) },
        ]}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile icon={Building2} label="Schools" value={fmtNum(schools.length)} sub="on the network" tone="forest" />
        <StatTile icon={Users} label="Students" value={fmtNum(stats?.totalStudentsReg ?? 0)} sub="across all schools" tone="terracotta" />
        <StatTile icon={Banknote} label="Volume" value={fmtUGX(stats?.totalVolume ?? 0)} sub="last 500 tx" tone="ochre" />
        <StatTile icon={TrendingUp} label="Fees" value={fmtUGX(stats?.totalFees ?? 0)} sub="platform share" tone="forest" />
      </div>
      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Panel title="Students by region" subtitle="Enrolment concentration across Uganda" icon={BarChart3}>
          <div className="space-y-3">
            {byRegion.length === 0 && <div className="text-sm text-muted-foreground">No regional data yet.</div>}
            {byRegion.map(([r, n], i) => (
              <BarRow key={r} label={r} value={n} max={maxRegion} tone={(["forest","ochre","terracotta"] as const)[i%3]} right={<span className="text-xs">{fmtNum(n)}</span>} />
            ))}
          </div>
        </Panel>
        <Panel title="Plan mix" subtitle="Subscription tiers across schools" icon={Wallet}>
          <div className="grid grid-cols-3 gap-3">
            {(["enterprise","standard","starter"] as const).map((p, i) => (
              <div key={p} className="rounded-xl border bg-card/60 p-3 text-center">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{p}</div>
                <div className="mt-2 font-display text-2xl" style={{ color: toneVar((["forest","ochre","terracotta"] as const)[i]) }}>{byPlan[p]}</div>
                <div className="text-[11px] text-muted-foreground">schools</div>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground">
            Enterprise schools unlock AI copilot, BI studio, SSO and a 4-hour SLA.
          </div>
        </Panel>
      </div>
      <Panel title="One-click exports" subtitle="Every export writes an audit record" icon={Download}>
        <div className="grid gap-3 sm:grid-cols-3">
          <button onClick={exportEnrollmentCsv} className="rounded-xl border bg-card p-4 text-left transition hover:border-[var(--forest)] hover:bg-[var(--forest)]/5">
            <div className="flex items-center gap-2"><span className="grid h-9 w-9 place-items-center rounded-lg bg-[var(--forest)]/10 text-[var(--forest)]"><Users className="h-4 w-4" /></span><div className="font-display text-sm">Enrollment CSV</div></div>
            <div className="mt-2 text-[11px] text-muted-foreground">{schools.length} schools · region, plan, headcount</div>
          </button>
          <button onClick={exportFinanceCsv} className="rounded-xl border bg-card p-4 text-left transition hover:border-[var(--ochre)] hover:bg-[var(--ochre)]/5">
            <div className="flex items-center gap-2"><span className="grid h-9 w-9 place-items-center rounded-lg bg-[var(--ochre)]/10 text-[var(--ochre)]"><Banknote className="h-4 w-4" /></span><div className="font-display text-sm">Finance CSV</div></div>
            <div className="mt-2 text-[11px] text-muted-foreground">Last 2,000 transactions with fees</div>
          </button>
          <button onClick={() => toast.message("MoE PDF pack queued — you will receive a link when ready.")} className="rounded-xl border bg-card p-4 text-left transition hover:border-[var(--terracotta)] hover:bg-[var(--terracotta)]/5">
            <div className="flex items-center gap-2"><span className="grid h-9 w-9 place-items-center rounded-lg bg-[var(--terracotta)]/10 text-[var(--terracotta)]"><FileText className="h-4 w-4" /></span><div className="font-display text-sm">MoE PDF pack</div></div>
            <div className="mt-2 text-[11px] text-muted-foreground">Regulator-formatted Ministry of Education bundle</div>
          </button>
        </div>
      </Panel>
    </div>
  );
}

function AISection({ audit }: { audit: any[] }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [prompt, setPrompt] = useState("");
  const { data: insights = [] } = useQuery({
    queryKey: ["ai-insights"], refetchInterval: 6000,
    queryFn: async () => (await supabase.from("ai_insights").select("id, body, action_label, status, created_at, guardian_id").order("created_at", { ascending: false }).limit(30)).data ?? [],
  });

  const captureInsight = useMutation({
    mutationFn: async () => {
      if (!prompt.trim()) throw new Error("Ask something");
      const summary = `Q: ${prompt.trim()}\nA: (drafted) Review the metric and revisit in 24h. Related audit signals: ${audit.slice(0,3).map((a:any)=>a.action).join(", ") || "none"}.`;
      const { error } = await supabase.from("ai_insights").insert({ guardian_id: user!.id, body: summary, action_label: "review", status: "open" });
      if (error) throw error;
      await supabase.from("audit_logs").insert({ actor_id: user!.id, action: "ai.query_logged", metadata: { chars: prompt.length } });
    },
    onSuccess: () => { toast.success("Insight captured"); setPrompt(""); qc.invalidateQueries({ queryKey: ["ai-insights"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const openCount = insights.filter((i: any) => i.status === "open").length;
  const contributors = new Set(insights.map((i: any) => i.guardian_id)).size;
  const suggestions = [
    { q: "Which schools showed a drop in attendance this week?", i: Activity },
    { q: "Fees still owed this term", i: Banknote },
    { q: "Fleet on-time %", i: Bus },
    { q: "Boarding welfare risks", i: HeartPulse },
    { q: "Merchant cash-flow anomalies", i: Store },
    { q: "Underperforming subjects", i: GraduationCap },
  ];

  return (
    <div className="space-y-6">
      <SectionHero
        eyebrow="Engagement · intelligence"
        title="AI copilot · TriSphere mind"
        subtitle="Ask anything about the network. Prompts and answers are captured as auditable insights and shared with the executive team."
        icon={Bot} tone="ink"
        chips={[
          { label: "Insights", value: fmtNum(insights.length) },
          { label: "Open follow-ups", value: fmtNum(openCount) },
          { label: "Contributors", value: fmtNum(contributors) },
          { label: "Audit signals", value: fmtNum(audit.length) },
        ]}
        accent={<div className="grid h-24 w-24 place-items-center rounded-2xl bg-white/10 backdrop-blur"><Bot className="h-10 w-10 text-white" /></div>}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile icon={Bot} label="Captured insights" value={fmtNum(insights.length)} sub="auditable answers" tone="forest" />
        <StatTile icon={Sparkles} label="Open follow-ups" value={fmtNum(openCount)} sub="awaiting review" tone="ochre" />
        <StatTile icon={Users} label="Contributors" value={fmtNum(contributors)} sub="distinct askers" tone="terracotta" />
        <StatTile icon={Activity} label="Audit signals" value={fmtNum(audit.length)} sub="powering context" tone="forest" />
      </div>
      <div className="grid gap-4 lg:grid-cols-[1fr_1.2fr]">
        <Panel title="Ask the copilot" subtitle="Prompts appear in the review feed and are audited" icon={Sparkles}>
          <Textarea rows={5} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. Which schools showed a drop in attendance this week?" />
          <Button className="mt-3 w-full" onClick={() => captureInsight.mutate()} disabled={captureInsight.isPending}><Sparkles className="mr-1 h-4 w-4" />{captureInsight.isPending ? "Thinking…" : "Ask copilot"}</Button>
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Suggested prompts</div>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {suggestions.map(s => (
                <button key={s.q} onClick={() => setPrompt(s.q)} className="group flex items-start gap-2 rounded-lg border bg-card p-2.5 text-left text-xs transition hover:border-[var(--forest)] hover:bg-[var(--forest)]/5">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-[var(--forest)]/10 text-[var(--forest)]"><s.i className="h-3.5 w-3.5" /></span>
                  <span className="leading-tight">{s.q}</span>
                </button>
              ))}
            </div>
          </div>
        </Panel>
        <Panel title="Insight feed" subtitle="Captured Q&A stream" icon={Bot}
          badge={<Badge className="ml-2 bg-success/15 text-success-foreground"><LiveDot /><span className="ml-1">live</span></Badge>}>
          <div className="max-h-[440px] space-y-2 overflow-auto pr-1">
            {insights.length === 0 && <div className="py-10 text-center text-sm text-muted-foreground">No insights captured yet.</div>}
            {(insights as any[]).map((i) => (
              <motion.div key={i.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border-l-4 bg-card/60 p-3 text-xs" style={{ borderLeftColor: i.status === "open" ? "var(--terracotta)" : "var(--forest)" }}>
                <div className="flex items-center justify-between"><Badge variant={i.status === "open" ? "destructive" : "secondary"}>{i.status}</Badge><div className="text-[10px] text-muted-foreground">{shortDateTime(i.created_at)}</div></div>
                <div className="mt-2 whitespace-pre-wrap leading-relaxed">{i.body}</div>
              </motion.div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}


// ============================================================
// God mode — audited emergency actions + feature flags
// ============================================================
function GodModeSection({ setRole }: { setRole: (r: string) => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [flags, setFlags] = useState<Record<string, boolean>>(() => {
    if (typeof window === "undefined") return { ai_copilot: true, marketplace: true, transport_live: true, boarding: true };
    try { return JSON.parse(localStorage.getItem("schoolos.flags") ?? "") || { ai_copilot: true, marketplace: true, transport_live: true, boarding: true }; }
    catch { return { ai_copilot: true, marketplace: true, transport_live: true, boarding: true }; }
  });
  const toggleFlag = async (k: string, v: boolean) => {
    const next = { ...flags, [k]: v };
    setFlags(next);
    localStorage.setItem("schoolos.flags", JSON.stringify(next));
    await supabase.from("audit_logs").insert({ actor_id: user!.id, action: "godmode.flag_toggled", metadata: { flag: k, value: v } });
    toast.success(`${k} → ${v ? "on" : "off"}`);
    qc.invalidateQueries({ queryKey: ["audit"] });
  };

  const [annTitle, setAnnTitle] = useState("");
  const [annBody, setAnnBody] = useState("");
  const sendAnnouncement = useMutation({
    mutationFn: async () => {
      if (!annTitle.trim()) throw new Error("Title required");
      const { data: users } = await supabase.from("profiles").select("id");
      const rows = (users ?? []).map((u) => ({ user_id: u.id, kind: "godmode", title: annTitle, body: annBody || null }));
      const { error } = await supabase.from("notifications").insert(rows);
      if (error) throw error;
      await supabase.from("audit_logs").insert({ actor_id: user!.id, action: "godmode.announcement", metadata: { title: annTitle, recipients: rows.length } });
      return rows.length;
    },
    onSuccess: (n) => { toast.success(`Announcement pushed to ${n} inboxes`); setAnnTitle(""); setAnnBody(""); qc.invalidateQueries({ queryKey: ["notifications-all"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const audited = async (label: string) => {
    await supabase.from("audit_logs").insert({ actor_id: user!.id, action: `godmode.${label}` });
    toast.success(`Recorded · ${label}`);
    qc.invalidateQueries({ queryKey: ["audit"] });
  };

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden shadow-card">
        <div className="gradient-forest p-6 text-white">
          <div className="flex items-center gap-2"><Crown className="h-5 w-5" /><div className="text-[10px] uppercase tracking-[0.22em] text-white/70">Super admin</div></div>
          <h2 className="mt-1 font-display text-3xl">God mode</h2>
          <p className="text-white/80">Every action is audited and immutable.</p>
        </div>
        <div className="grid gap-3 p-6 sm:grid-cols-2">
          <Button variant="outline" onClick={() => setRole("parent")}><Eye className="mr-1 h-4 w-4" />Impersonate Parent</Button>
          <Button variant="outline" onClick={() => setRole("student")}><Eye className="mr-1 h-4 w-4" />Impersonate Student</Button>
          <Button variant="outline" onClick={() => setRole("merchant")}><Eye className="mr-1 h-4 w-4" />Impersonate Merchant</Button>
          <Button variant="outline" onClick={() => audited("force_password_reset_queued")}><RefreshCw className="mr-1 h-4 w-4" />Force password reset (all admins)</Button>
          <Button variant="destructive" onClick={() => audited("emergency_lock_school")}><Lock className="mr-1 h-4 w-4" />Emergency lock school</Button>
          <Button variant="destructive" onClick={() => audited("emergency_lock_user")}><Lock className="mr-1 h-4 w-4" />Emergency lock user</Button>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5 shadow-card">
          <div className="font-display text-xl">Global feature flags</div>
          <div className="mt-3 space-y-3">
            {Object.entries(flags).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between rounded-lg border p-3">
                <div><div className="text-sm font-medium">{k.replace(/_/g, " ")}</div><div className="text-[11px] text-muted-foreground">Toggle affects every school immediately.</div></div>
                <Switch checked={v} onCheckedChange={(val) => toggleFlag(k, val)} />
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-5 shadow-card">
          <div className="font-display text-xl">Global announcement</div>
          <div className="mt-3 space-y-3">
            <div><Label className="text-[10px] uppercase">Title</Label><Input value={annTitle} onChange={(e) => setAnnTitle(e.target.value)} placeholder="Nationwide notice…" /></div>
            <div><Label className="text-[10px] uppercase">Body</Label><Textarea rows={3} value={annBody} onChange={(e) => setAnnBody(e.target.value)} /></div>
            <Button className="w-full" onClick={() => sendAnnouncement.mutate()} disabled={sendAnnouncement.isPending}><MessageSquare className="mr-1 h-4 w-4" />{sendAnnouncement.isPending ? "Publishing…" : "Push to every user"}</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}


// ============================================================
// Tiny shared bits
// ============================================================
// ── Primitive helpers ─────────────────────────────────────────────────────────
function KPI({ i: Icon, label, value }: { i: any; label: string; value: any }) {
  return <StatTile icon={Icon} label={label} value={value} />;
}
function GatewayCard({ name, status, success }: { name: string; status: string; success: number }) {
  const ok = success >= 95;
  return (
    <div className="rounded-xl border bg-muted/30 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-[13px]">{name}</span>
        <StatusPill status={ok ? "healthy" : "degraded"} />
      </div>
      <div>
        <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1.5">
          <span>Success rate</span><span className="font-mono">{success}%</span>
        </div>
        <HealthBar value={success} />
      </div>
    </div>
  );
}
function UsageBar({ label, pct }: { label: string; pct: number }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-[12px]">
        <span className="text-foreground">{label}</span>
        <span className="text-muted-foreground font-mono">{pct}%</span>
      </div>
      <HealthBar value={pct} />
    </div>
  );
}
function FeeBadge({ label, sub, pct, tone }: { label: string; sub: string; pct: string; tone: "terracotta" | "ochre" | "forest" }) {
  const colors = {
    terracotta: { bg: "#fff1ee", text: "#c94b3a", border: "#f5c4bc" },
    ochre:      { bg: "#fffbeb", text: "#b45309", border: "#fde68a" },
    forest:     { bg: "#f0fdf4", text: "#16a34a", border: "#bbf7d0" },
  };
  const c = colors[tone];
  return (
    <div className="rounded-xl border p-4" style={{ background: c.bg, borderColor: c.border }}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: c.text }}>{label}</span>
        <span className="text-[13px] font-bold" style={{ color: c.text }}>{pct}</span>
      </div>
      <p className="text-[11px] text-muted-foreground">{sub}</p>
    </div>
  );
}
function ModulePlaceholder({ title, desc, kpis, actions }: { title: string; desc: string; kpis: { i: any; l: string; v: any }[]; actions: string[] }) {
  return (
    <div className="space-y-5">
      <SectionHeader
        title={title}
        subtitle={desc}
        action={
          <div className="flex flex-wrap gap-2">
            {actions.map(a => <Button key={a} variant="outline" size="sm">{a}</Button>)}
          </div>
        }
      />
      {kpis.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {kpis.map((k) => <StatTile key={k.l} icon={k.i} label={k.l} value={k.v} />)}
        </div>
      )}
    </div>
  );
}

// ── Audit export panel ────────────────────────────────────────────────────────
type ExportOpts = { from?: string; to?: string; action?: string; format: "csv" | "pdf"; label: string };
function AuditExportPanel({ exportAudit, audit }: { exportAudit: (o: ExportOpts) => Promise<void>; audit: any[] }) {
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(weekAgo);
  const [to, setTo] = useState(today);
  const [action, setAction] = useState<string>("__all");
  const [format, setFormat] = useState<"csv" | "pdf">("csv");
  const actionPrefixes = useMemo(() => {
    const set = new Set<string>();
    audit.forEach((a: any) => { const p = (a.action ?? "").split(".")[0]; if (p) set.add(p); });
    return Array.from(set).sort();
  }, [audit]);
  const run = () => exportAudit({ from, to, action: action === "__all" ? undefined : action, format, label: `${from}_to_${to}` });
  return (
    <div className="rounded-2xl border bg-card p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-semibold flex items-center gap-2"><Archive className="h-4 w-4" /> Filtered audit export</p>
          <p className="text-[12px] text-muted-foreground mt-0.5">Pick a date window and action type. The full filtered set is exported.</p>
        </div>
        <Badge variant="secondary" className="shrink-0"><Shield className="mr-1 h-3 w-3" />Append-only</Badge>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <div><Label className="text-[10px] font-bold uppercase tracking-widest mb-1.5 block">From</Label><Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-9 text-sm" /></div>
        <div><Label className="text-[10px] font-bold uppercase tracking-widest mb-1.5 block">To</Label><Input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-9 text-sm" /></div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest mb-1.5 block">Action type</Label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All actions</SelectItem>
              {actionPrefixes.map(p => <SelectItem key={p} value={p}>{p}.*</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest mb-1.5 block">Format</Label>
          <Select value={format} onValueChange={(v) => setFormat(v as "csv" | "pdf")}>
            <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="csv">CSV · machine-readable</SelectItem>
              <SelectItem value="pdf">PDF · regulator-friendly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <Button className="w-full h-9 bg-[var(--forest)] hover:opacity-90 text-white text-sm gap-1.5" onClick={run}>
            {format === "csv" ? <Download className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}Export
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 pt-1 border-t">
        {[
          { label: "Today",      from: today,  to: today },
          { label: "Last 7 days", from: weekAgo, to: today },
          { label: "Last 30 days", from: new Date(Date.now()-30*86400000).toISOString().slice(0,10), to: today },
          { label: "10-yr archive", from: new Date(Date.now()-10*365*86400000).toISOString().slice(0,10), to: today },
        ].map(q => (
          <Button key={q.label} size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => { setFrom(q.from); setTo(q.to); }}>{q.label}</Button>
        ))}
      </div>
    </div>
  );
}
